import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Upload, Plus, Check, X, Clock, Calendar, Eye, Edit3, ChevronDown, ChevronUp, Search, Lock, ShieldCheck, AlertCircle, CheckCircle, Trash2, Loader, Sparkles } from 'lucide-react';
import { vendorService, authService } from '../services/api';
import { fetchStates as getCleanStates, fetchCitiesForState as getCleanCitiesForState } from '../services/locationService';

import { useToast } from '../hooks/useToast';
import authStorage from '../utils/authStorage';
import { compressImageIfNeeded, compressManyImagesIfNeeded, formatFileSize, isPDF } from '../utils/mediaCompressor';

const isEnglishOnly = (str) => {
  if (!str) return true;
  return /^[\x20-\x7E\s]*$/.test(str);
};

const TIME_OPTIONS = [
  "Select Time",
  "12:00 AM", "12:30 AM", "01:00 AM", "01:30 AM", "02:00 AM", "02:30 AM",
  "03:00 AM", "03:30 AM", "04:00 AM", "04:30 AM", "05:00 AM", "05:30 AM",
  "06:00 AM", "06:30 AM", "07:00 AM", "07:30 AM", "08:00 AM", "08:30 AM",
  "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "01:00 PM", "01:30 PM", "02:00 PM", "02:30 PM",
  "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM", "05:00 PM", "05:30 PM",
  "06:00 PM", "06:30 PM", "07:00 PM", "07:30 PM", "08:00 PM", "08:30 PM",
  "09:00 PM", "09:30 PM", "10:00 PM", "10:30 PM", "11:00 PM", "11:30 PM"
];

const DAYS_OF_WEEK = ["Day", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const LABEL_CLASS = "block text-xs font-semibold text-gray-700 mb-1";
const INPUT_CLASS = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 focus:border-[#2ab391] bg-white transition-all shadow-xs text-gray-750 h-[38px] md:h-[40px]";

const formatTimeForBackend = (timeStr) => {
  if (!timeStr || timeStr === "Select Time" || timeStr === "Day") return null;
  const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/);
  if (!match) return null;
  let [, hours, minutes, modifier] = match;
  hours = parseInt(hours, 10);
  if (hours === 12 && modifier === 'AM') hours = 0;
  if (hours !== 12 && modifier === 'PM') hours += 12;
  return `${hours.toString().padStart(2, '0')}:${minutes}:00`;
};

const formatTimeForFrontend = (timeStr) => {
  if (!timeStr || timeStr === "Select Time") return "Select Time";
  if (timeStr.includes("AM") || timeStr.includes("PM")) return timeStr;

  const match = timeStr.match(/^(\d{2}):(\d{2})/);
  if (!match) return "Select Time";

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const modifier = hours >= 12 ? "PM" : "AM";

  if (hours > 12) hours -= 12;
  if (hours === 0) hours = 12;

  return `${hours.toString().padStart(2, '0')}:${minutes} ${modifier}`;
};

/**
 * Calculates Profile Completion Percentage (0 - 100%) for Company and Individual Business listings.
 * Works seamlessly with both backend listing entities (with integers/snake_case/nested objects)
 * and active frontend form state.
 */
export const calculateListingCompleteness = (data, isIndividualType) => {
  if (!data) {
    return {
      percentage: 0,
      items: [],
      completedItems: [],
      missingItems: [],
      completedCount: 0,
      totalItems: 0,
      statusText: 'Incomplete',
      badgeColor: 'text-amber-700 bg-amber-50 border-amber-200',
      progressColor: 'bg-amber-500'
    };
  }

  const isIndividual = isIndividualType !== undefined
    ? Boolean(isIndividualType)
    : ((data.listingType || data.listing_type || '').toUpperCase() === 'INDIVIDUAL');

  const items = [];

  // Robust validation helpers handling strings, numbers, booleans, arrays, files, and null/undefined values
  const hasText = (val, minLen = 1) => {
    if (val === null || val === undefined) return false;
    const str = String(val).trim();
    if (str === '' || str.toLowerCase() === 'null' || str.toLowerCase() === 'undefined') return false;
    return str.length >= minLen;
  };

  const hasPhone = (val) => {
    if (val === null || val === undefined) return false;
    const digits = String(val).replace(/\D/g, '');
    return digits.length >= 10 && digits.length <= 12;
  };

  const hasEmail = (val) => {
    if (val === null || val === undefined) return false;
    const str = String(val).trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);
  };

  const hasArray = (arr) => {
    if (!arr) return false;
    if (Array.isArray(arr)) return arr.length > 0;
    if (typeof arr === 'string') {
      const trimmed = arr.trim();
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed) && parsed.length > 0;
        } catch (e) {
          return false;
        }
      }
      return trimmed.length > 0 && trimmed !== '[]';
    }
    return false;
  };

  const hasMedia = (val) => {
    if (!val) return false;
    if (typeof val === 'string') {
      const str = val.trim();
      return str !== '' && str.toLowerCase() !== 'null' && str.toLowerCase() !== 'undefined';
    }
    // Handles File, Blob, or object instances
    return true;
  };

  if (isIndividual) {
    // INDIVIDUAL BUSINESS BREAKDOWN (100% Total)
    // 1. Full Name (10%)
    const hasName = hasText(data.companyName || data.company_name || data.name, 2);
    items.push({ id: 'name', label: 'Add Full Name', points: 10, isComplete: hasName, category: 'core', fieldName: 'companyName' });

    // 2. Phone (10%)
    const hasPhoneVal = hasPhone(data.phone || data.mobileNo || data.mobile_no || data.contactNumber || data.contact_number);
    items.push({ id: 'phone', label: 'Add Contact Number', points: 10, isComplete: hasPhoneVal, category: 'core', fieldName: 'phone' });

    // 3. Email (10%)
    const hasEmailVal = hasEmail(data.email || data.emailId || data.email_id);
    items.push({ id: 'email', label: 'Add Email ID', points: 10, isComplete: hasEmailVal, category: 'core', fieldName: 'email' });

    // 4. State & City (10%)
    const hasLocation = hasText(data.state) && hasText(data.city);
    items.push({ id: 'location', label: 'Select State & City', points: 10, isComplete: hasLocation, category: 'core', fieldName: 'state' });

    // 5. Area (10%)
    const hasArea = hasText(data.area);
    items.push({ id: 'area', label: 'Add Area / Locality', points: 10, isComplete: hasArea, category: 'core', fieldName: 'area' });

    // 6. Category (10%)
    const hasCat = Boolean(data.category || data.categoryId || data.category_id || data.categoryName || data.category_name);
    items.push({ id: 'category', label: 'Select Category', points: 10, isComplete: hasCat, category: 'core', fieldName: 'category' });

    // 7. Legal Proof (10%)
    const hasProof = hasMedia(data.govtProof || data.govtProofUrl || data.govt_proof_url || data.legalProof || data.legalProofUrl || data.legal_proof_url);
    items.push({ id: 'govtProof', label: 'Add Aadhaar / PAN Proof', points: 10, isComplete: hasProof, category: 'media', fieldName: 'govtProof' });

    // 8. Gallery Photos (10%)
    const hasGallery = hasArray(data.gallery || data.galleryImages || data.galleryImageUrls || data.gallery_image_urls || data.images || data.gallery_photos);
    items.push({ id: 'gallery', label: 'Add Portfolio Photos', points: 10, isComplete: hasGallery, category: 'media', fieldName: 'galleryImages' });

    // 9. Instagram (5%)
    const hasInsta = hasText(data.instagram);
    items.push({ id: 'instagram', label: 'Add Instagram', points: 5, isComplete: hasInsta, category: 'social', fieldName: 'instagram' });

    // 10. Facebook (5%)
    const hasFb = hasText(data.facebook);
    items.push({ id: 'facebook', label: 'Add Facebook', points: 5, isComplete: hasFb, category: 'social', fieldName: 'facebook' });

    // 11. LinkedIn (5%)
    const hasLi = hasText(data.linkedin);
    items.push({ id: 'linkedin', label: 'Add LinkedIn', points: 5, isComplete: hasLi, category: 'social', fieldName: 'linkedin' });

    // 12. X / Twitter (5%)
    const hasTw = hasText(data.twitter || data.twitterUrl || data.twitter_url || data.xProfile || data.x_profile);
    items.push({ id: 'twitter', label: 'Add X (Twitter)', points: 5, isComplete: hasTw, category: 'social', fieldName: 'twitter' });

  } else {
    // COMPANY BUSINESS BREAKDOWN (100% Total)
    // Core Details (35%)
    const hasName = hasText(data.companyName || data.company_name || data.name, 2);
    items.push({ id: 'companyName', label: 'Add Company Name', points: 4, isComplete: hasName, category: 'core', fieldName: 'companyName' });

    const hasAbout = hasText(data.aboutCompany || data.about_company || data.about, 10);
    items.push({ id: 'aboutCompany', label: 'Add About Company', points: 4, isComplete: hasAbout, category: 'core', fieldName: 'aboutCompany' });

    const hasPhoneVal = hasPhone(data.phone || data.mobileNo || data.mobile_no || data.contactNumber || data.contact_number);
    items.push({ id: 'phone', label: 'Add Contact Phone', points: 4, isComplete: hasPhoneVal, category: 'core', fieldName: 'phone' });

    const hasEmailVal = hasEmail(data.email || data.emailId || data.email_id);
    items.push({ id: 'email', label: 'Add Email ID', points: 4, isComplete: hasEmailVal, category: 'core', fieldName: 'email' });

    const hasAddr = hasText(data.address);
    items.push({ id: 'address', label: 'Add Address', points: 4, isComplete: hasAddr, category: 'core', fieldName: 'address' });

    const hasLoc = hasText(data.state) && hasText(data.city);
    items.push({ id: 'location', label: 'Add State & City', points: 5, isComplete: hasLoc, category: 'core', fieldName: 'state' });

    const hasArea = hasText(data.area);
    items.push({ id: 'area', label: 'Add Area / Locality', points: 4, isComplete: hasArea, category: 'core', fieldName: 'area' });

    const hasZip = hasText(data.zipcode || data.pincode || data.pinCode || data.zip_code, 4);
    items.push({ id: 'zipcode', label: 'Add Zipcode', points: 3, isComplete: hasZip, category: 'core', fieldName: 'zipcode' });

    const hasCatAndTags = Boolean(data.category || data.categoryId || data.category_id || data.categoryName || data.category_name) &&
      hasArray(data.tags || data.selectedTags || data.tagIds || data.tag_ids);
    items.push({ id: 'categoryTags', label: 'Add Category & Tags', points: 3, isComplete: hasCatAndTags, category: 'core', fieldName: 'category' });

    // Media & Documents (20%)
    const hasLogo = hasMedia(data.logo || data.brandLogo || data.logoUrl || data.logo_url || data.logo_image_url || data.logoImageUrl);
    items.push({ id: 'brandLogo', label: 'Add Brand Logo', points: 5, isComplete: hasLogo, category: 'media', fieldName: 'brandLogo' });

    const hasProof = hasMedia(data.govtProof || data.govtProofUrl || data.govt_proof_url || data.legalProof || data.legalProofUrl || data.legal_proof_url);
    items.push({ id: 'govtProof', label: 'Add Legal Document', points: 5, isComplete: hasProof, category: 'media', fieldName: 'govtProof' });

    const hasBanner = hasMedia(data.banner || data.profileBanner || data.profileBannerUrl || data.profile_banner_url || data.bannerUrl || data.banner_url);
    items.push({ id: 'profileBanner', label: 'Add Profile Banner', points: 5, isComplete: hasBanner, category: 'media', fieldName: 'profileBanner' });

    const hasGallery = hasArray(data.gallery || data.galleryImages || data.galleryImageUrls || data.gallery_image_urls || data.images || data.gallery_photos);
    items.push({ id: 'gallery', label: 'Add Gallery Photos', points: 5, isComplete: hasGallery, category: 'media', fieldName: 'galleryImages' });

    // Business Info (25%)
    const hasWeb = hasText(data.website || data.websiteUrl || data.website_url);
    items.push({ id: 'website', label: 'Add Website', points: 5, isComplete: hasWeb, category: 'business', fieldName: 'website' });

    const hasEstd = hasText(data.establishedSince || data.established_since || data.establishedYear || data.established_year);
    items.push({ id: 'establishedSince', label: 'Add Estd Year', points: 5, isComplete: hasEstd, category: 'business', fieldName: 'establishedSince' });

    const hasGst = hasText(data.gstin || data.gst_number || data.gstNumber);
    items.push({ id: 'gstin', label: 'Add GSTIN', points: 5, isComplete: hasGst, category: 'business', fieldName: 'gstin' });

    const hasBrands = hasArray(data.brands || data.selectedBrands || data.brandIds || data.brand_ids);
    items.push({ id: 'brands', label: 'Add Brands', points: 5, isComplete: hasBrands, category: 'business', fieldName: 'brands' });

    const hasHours = Boolean(
      (data.businessHoursType && (data.businessHoursType === 'MON_TO_SAT' || data.businessHoursType === 'MON_TO_SUN' || (data.businessHoursType === 'CUSTOM' && Array.isArray(data.businessHours?.customHours) && data.businessHours.customHours.length > 0))) ||
      (data.businessHours && typeof data.businessHours === 'object' && (
        data.businessHours.type === 'MON_TO_SAT' ||
        data.businessHours.type === 'MON_TO_SUN' ||
        (data.businessHours.mainHours && data.businessHours.mainHours.openTime && data.businessHours.mainHours.openTime !== 'Select Time') ||
        (Array.isArray(data.businessHours.customHours) && data.businessHours.customHours.length > 0)
      )) ||
      (data.business_hours && typeof data.business_hours === 'object' && (
        data.business_hours.type === 'MON_TO_SAT' ||
        data.business_hours.type === 'MON_TO_SUN' ||
        (data.business_hours.mainHours && data.business_hours.mainHours.openTime && data.business_hours.mainHours.openTime !== 'Select Time') ||
        (Array.isArray(data.business_hours.customHours) && data.business_hours.customHours.length > 0)
      )) ||
      (typeof data.business_hours_json === 'string' && data.business_hours_json.trim().length > 2) ||
      (typeof data.businessHoursJson === 'string' && data.businessHoursJson.trim().length > 2)
    );
    items.push({ id: 'businessHours', label: 'Set Working Hours', points: 5, isComplete: hasHours, category: 'business', fieldName: 'businessHours' });

    // Social Profiles (20%)
    const hasInsta = hasText(data.instagram);
    items.push({ id: 'instagram', label: 'Add Instagram', points: 5, isComplete: hasInsta, category: 'social', fieldName: 'instagram' });

    const hasFb = hasText(data.facebook);
    items.push({ id: 'facebook', label: 'Add Facebook', points: 5, isComplete: hasFb, category: 'social', fieldName: 'facebook' });

    const hasLi = hasText(data.linkedin);
    items.push({ id: 'linkedin', label: 'Add LinkedIn', points: 5, isComplete: hasLi, category: 'social', fieldName: 'linkedin' });

    const hasTw = hasText(data.twitter || data.twitterUrl || data.twitter_url || data.xProfile || data.x_profile);
    items.push({ id: 'twitter', label: 'Add X (Twitter)', points: 5, isComplete: hasTw, category: 'social', fieldName: 'twitter' });
  }

  const completedItems = items.filter(item => item.isComplete);
  const missingItems = items.filter(item => !item.isComplete);
  const percentage = Math.min(100, completedItems.reduce((sum, item) => sum + item.points, 0));

  let statusText = 'Incomplete';
  let badgeColor = 'bg-rose-50 text-rose-700 border-rose-200';
  let iconBg = 'bg-rose-50 text-rose-600';
  let progressColor = 'bg-rose-500';
  let chipPointColor = 'text-rose-600';

  if (percentage >= 100) {
    statusText = 'Complete';
    badgeColor = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    iconBg = 'bg-emerald-50 text-emerald-600';
    progressColor = 'bg-emerald-500';
    chipPointColor = 'text-emerald-600';
  } else if (percentage >= 75) {
    statusText = 'Good Profile';
    badgeColor = 'bg-teal-50 text-teal-700 border-teal-200';
    iconBg = 'bg-teal-50 text-[#2ab391]';
    progressColor = 'bg-[#2ab391]';
    chipPointColor = 'text-[#2ab391]';
  } else if (percentage >= 50) {
    statusText = 'Intermediate';
    badgeColor = 'bg-amber-50 text-amber-700 border-amber-200';
    iconBg = 'bg-amber-50 text-amber-600';
    progressColor = 'bg-amber-500';
    chipPointColor = 'text-amber-600';
  }

  return {
    percentage,
    items,
    completedItems,
    missingItems,
    completedCount: completedItems.length,
    totalItems: items.length,
    statusText,
    badgeColor,
    iconBg,
    progressColor,
    chipPointColor
  };
};

const ManageListing = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const claimListingId = searchParams.get('claimListingId');

  const [claimantName, setClaimantName] = useState('');
  const [claimantPhone, setClaimantPhone] = useState('');
  const [claimantDesignation, setClaimantDesignation] = useState('');
  const [claimantNameError, setClaimantNameError] = useState('');
  const [claimantPhoneError, setClaimantPhoneError] = useState('');
  const [claimantDesignationError, setClaimantDesignationError] = useState('');
  const [showClaimModal, setShowClaimModal] = useState(false);

  // Media compression progress states
  const [compressingBanner, setCompressingBanner] = useState(null);
  const [compressingLogo, setCompressingLogo] = useState(null);
  const [compressingGovtProof, setCompressingGovtProof] = useState(null);
  const [compressingGallery, setCompressingGallery] = useState(null);
  const [compressingClaimProof, setCompressingClaimProof] = useState(null);

  const [showCategoryRequestModal, setShowCategoryRequestModal] = useState(false);
  const [reqCategoryName, setReqCategoryName] = useState('');
  const [reqCategoryDesc, setReqCategoryDesc] = useState('');
  const [reqCategoryNameError, setReqCategoryNameError] = useState('');
  const [reqCategoryDescError, setReqCategoryDescError] = useState('');
  const [submittingCategoryRequest, setSubmittingCategoryRequest] = useState(false);

  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [matchedListing, setMatchedListing] = useState(null);
  const [pendingClaim, setPendingClaim] = useState(null);
  const [claimComments, setClaimComments] = useState('');
  const [claimProofFile, setClaimProofFile] = useState(null);
  const [claimCommentsError, setClaimCommentsError] = useState('');
  const [claimProofFileError, setClaimProofFileError] = useState('');
  const [submittingClaim, setSubmittingClaim] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(false);

  // Admin draft states
  const [pendingAdminDraft, setPendingAdminDraft] = useState(null);
  const [showDraftPreviewModal, setShowDraftPreviewModal] = useState(false);
  const [draftRejectReason, setDraftRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveConfirmModal, setShowApproveConfirmModal] = useState(false);
  const [showRejectConfirmModal, setShowRejectConfirmModal] = useState(false);
  const [submittingDecision, setSubmittingDecision] = useState(false);
  const [userClaims, setUserClaims] = useState([]);

  // Deletion modal states
  const [showDeleteStep1Modal, setShowDeleteStep1Modal] = useState(false);
  const [showDeleteStep2Modal, setShowDeleteStep2Modal] = useState(false);
  const [deletingListing, setDeletingListing] = useState(null);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [deletingInProgress, setDeletingInProgress] = useState(false);

  const handleCategoryRequestSubmit = async (e) => {
    e.preventDefault();
    let hasError = false;
    if (!reqCategoryName.trim()) {
      setReqCategoryNameError('Category name is required.');
      hasError = true;
    } else if (!isEnglishOnly(reqCategoryName)) {
      setReqCategoryNameError('Please use English characters only (emojis and non-English scripts are not allowed).');
      hasError = true;
    } else {
      setReqCategoryNameError('');
    }

    if (!reqCategoryDesc.trim()) {
      setReqCategoryDescError('Reason/description is required.');
      hasError = true;
    } else if (reqCategoryDesc.trim().length < 10) {
      setReqCategoryDescError('Please provide a description of at least 10 characters.');
      hasError = true;
    } else if (!isEnglishOnly(reqCategoryDesc)) {
      setReqCategoryDescError('Please use English characters only (emojis and non-English scripts are not allowed).');
      hasError = true;
    } else {
      setReqCategoryDescError('');
    }

    if (hasError) return;

    setSubmittingCategoryRequest(true);
    try {
      const formData = new FormData();
      formData.append('description', reqCategoryDesc.trim());
      formData.append('ticket_type', 'CATEGORY_REQUEST');
      formData.append('requested_category', reqCategoryName.trim());
      formData.append('priority', 'MEDIUM');

      const res = await vendorService.createSupportTicket(formData);
      if (res.success) {
        toast.success("Category request submitted successfully! Admin will review it soon.");
        setShowCategoryRequestModal(false);
        setReqCategoryName('');
        setReqCategoryDesc('');
      } else {
        toast.error("Failed to submit request: " + (res.error || "Unknown error"));
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while submitting request.");
    } finally {
      setSubmittingCategoryRequest(false);
    }
  };

  const handleClaimSubmit = async (e) => {
    e.preventDefault();
    if (!matchedListing) return;

    let isValid = true;

    // Claimant Name Validation
    if (!claimantName.trim()) {
      setClaimantNameError("Full name is required.");
      isValid = false;
    } else if (claimantName.trim().length < 2) {
      setClaimantNameError("Full name must be at least 2 characters.");
      isValid = false;
    } else {
      setClaimantNameError("");
    }

    // Claimant Phone Validation
    if (!claimantPhone.trim()) {
      setClaimantPhoneError("Contact number is required.");
      isValid = false;
    } else if (!/^\d{10}$/.test(claimantPhone.trim())) {
      setClaimantPhoneError("Contact number must be exactly 10 digits.");
      isValid = false;
    } else {
      setClaimantPhoneError("");
    }

    // Claimant Designation Validation
    if (!claimantDesignation.trim()) {
      setClaimantDesignationError("Designation is required.");
      isValid = false;
    } else if (claimantDesignation.trim().length < 2) {
      setClaimantDesignationError("Designation must be at least 2 characters.");
      isValid = false;
    } else {
      setClaimantDesignationError("");
    }

    // Comments/description is optional
    setClaimCommentsError("");

    // Validate file upload: only required if we don't have a previous proof photo URL
    const hasPreviousProof = pendingClaim && pendingClaim.proofPhotoUrl;
    if (!claimProofFile && !hasPreviousProof) {
      setClaimProofFileError("Government proof document is required.");
      isValid = false;
    } else if (claimProofFile) {
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf'];
      const fileExt = claimProofFile.name.split('.').pop().toLowerCase();
      const allowedMimetypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];

      if (!allowedExtensions.includes(fileExt) || !allowedMimetypes.includes(claimProofFile.type)) {
        setClaimProofFileError("Proof document must be a JPG, PNG, or PDF file.");
        isValid = false;
      } else {
        setClaimProofFileError("");
      }
    } else {
      setClaimProofFileError("");
    }

    if (!isValid) {
      toast.error("Please resolve the validation errors before submitting.");
      return;
    }

    setSubmittingClaim(true);
    try {
      const formData = new FormData();
      formData.append("claimantName", claimantName);
      formData.append("claimantPhone", claimantPhone);
      formData.append("claimantDesignation", claimantDesignation);
      formData.append("comments", claimComments);
      if (claimProofFile) {
        formData.append("file", claimProofFile);
      }

      const res = await vendorService.claimListing(matchedListing.id, formData);
      if (res.success && res.data) {
        setPendingClaim(res.data);
        const claimsRes = await vendorService.getMyClaims();
        if (claimsRes.success && claimsRes.data) {
          setUserClaims(claimsRes.data);
        }
        setClaimComments('');
        setClaimProofFile(null);
        setClaimCommentsError('');
        setClaimProofFileError('');
        setClaimantDesignation('');
        setClaimantNameError('');
        setClaimantPhoneError('');
        setClaimantDesignationError('');
        setShowClaimModal(false);
        setSearchParams({}); // Clear query parameters
        toast.success("Claim request submitted successfully! Admin will verify and approve your request.");
      } else {
        toast.error(res.error || "Failed to submit claim request. Please try again.");
      }
    } catch (err) {
      console.error("Error submitting claim:", err);
      toast.error("An error occurred. Please try again.");
    } finally {
      setSubmittingClaim(false);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:8080' : '';
    if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
    return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
  };
  const [listingType, setListingType] = useState('COMPANY'); // 'COMPANY' vs 'INDIVIDUAL'
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [membershipPlan, setMembershipPlan] = useState('FREE');  // Real plan from backend
  const [membershipStatus, setMembershipStatus] = useState('ACTIVE');
  const [businessHoursType, setBusinessHoursType] = useState('MON_TO_SAT');
  const [myListings, setMyListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingListingId, setEditingListingId] = useState(null);
  const [tagSearch, setTagSearch] = useState('');
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [brandSearch, setBrandSearch] = useState('');

  const bannerInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const govtProofInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const [userProfile, setUserProfile] = useState(null);

  const [formData, setFormData] = useState({
    companyName: '',
    parentCompanyName: '',
    aboutCompany: '',
    phone: '',
    email: '',
    address: '',
    area: '',
    state: '',
    city: '',
    zipcode: '',
    website: '',
    gstin: '',
    establishedSince: '',
    instagram: '',
    linkedin: '',
    facebook: '',
    twitter: ''
  });

  const [profileBanner, setProfileBanner] = useState(null);
  const [brandLogo, setBrandLogo] = useState(null);
  const [govtProof, setGovtProof] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  // Map of existing gallery URL strings -> their file size in bytes (fetched via HEAD)
  const [galleryUrlSizes, setGalleryUrlSizes] = useState({});
  const [fetchingGallerySizes, setFetchingGallerySizes] = useState(false);

  const [businessHours, setBusinessHours] = useState({
    mainHours: { openTime: '09:00 AM', closeTime: '06:00 PM' },
    saturdayHours: { openTime: 'Select Time', closeTime: 'Select Time' },
    sundayHours: { openTime: 'Select Time', closeTime: 'Select Time' },
    customHours: []
  });

  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  const [apiStates, setApiStates] = useState([]);
  const [apiCities, setApiCities] = useState([]);
  const [apiError, setApiError] = useState('');
  const [loadingCities, setLoadingCities] = useState(false);

  const yearRef = useRef(null);
  const stateRef = useRef(null);
  const cityRef = useRef(null);

  const [isSubmitted, setIsSubmitted] = useState(false);

  const [touched, setTouched] = useState({
    companyName: false,
    aboutCompany: false,
    phone: false,
    email: false,
    address: false,
    area: false,
    state: false,
    city: false,
    zipcode: false,
    gstin: false,
    category: false,
    tags: false,
    profileBanner: false,
    brandLogo: false,
    govtProof: false
  });

  // Quick jump & focus handler for missing fields
  const handleQuickJumpToField = async (listing, targetItem) => {
    if (!isFormOpen && listing) {
      await handleEdit(listing);
    }
    const isSocial = ['instagram', 'facebook', 'linkedin', 'twitter'].includes(targetItem.id || targetItem.fieldName);
    const isHours = targetItem.id === 'businessHours';
    if (isSocial || isHours) {
      setShowAdditionalDetails(true);
    }

    setTimeout(() => {
      if (targetItem.fieldName === 'profileBanner') {
        bannerInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (targetItem.fieldName === 'brandLogo') {
        logoInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (targetItem.fieldName === 'govtProof') {
        govtProofInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (targetItem.fieldName === 'galleryImages') {
        galleryInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (targetItem.fieldName === 'brands') {
        const brandEl = document.querySelector('input[name="brands"]');
        if (brandEl) {
          brandEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          brandEl.focus();
        }
      } else if (targetItem.fieldName === 'establishedSince') {
        if (yearRef.current) {
          yearRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const yearInput = yearRef.current.querySelector('input');
          if (yearInput) yearInput.focus();
        }
      } else if (targetItem.fieldName) {
        const el = document.querySelector(`[name="${targetItem.fieldName}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus();
        }
      }
    }, 180);
  };

  const validate = (data, cat, tags, banner, logo, govtProof, isEdit) => {
    const errors = {};

    if (listingType === 'INDIVIDUAL') {
      if (!data.companyName || !data.companyName.trim()) {
        errors.companyName = "Name is required.";
      } else if (data.companyName.trim().length < 2) {
        errors.companyName = "Name must be at least 2 characters.";
      } else if (!isEnglishOnly(data.companyName)) {
        errors.companyName = "Please use English characters only.";
      }

      if (!data.phone || !data.phone.trim()) {
        errors.phone = "Contact number is required.";
      } else if (!/^\d{10,12}$/.test(data.phone.trim())) {
        errors.phone = "Contact number must be 10, 11 or 12 digits.";
      }

      if (!data.email || !data.email.trim()) {
        errors.email = "Email address is required.";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) {
        errors.email = "Please enter a valid email address.";
      }

      if (!data.state || !data.state.trim()) {
        errors.state = "State is required.";
      }

      if (!data.city || !data.city.trim()) {
        errors.city = "City is required.";
      }

      if (!data.area || !data.area.trim()) {
        errors.area = "Area is required.";
      } else if (!isEnglishOnly(data.area)) {
        errors.area = "Please use English characters only.";
      }

      if (!cat) {
        errors.category = "Category is required.";
      }

      if (!govtProof && !isEdit) {
        errors.govtProof = "Legal Documents proof (Aadhaar card/PAN Card) is required.";
      } else if (govtProof && typeof govtProof !== 'string' && govtProof.name) {
        const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf'];
        const ext = govtProof.name.split('.').pop().toLowerCase();
        const allowedMimes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
        if (!allowedExtensions.includes(ext) || (govtProof.type && !allowedMimes.includes(govtProof.type))) {
          errors.govtProof = "Legal Document must be a valid PNG, JPG, JPEG, or PDF file.";
        }
      }

      return errors;
    }

    if (!data.companyName || !data.companyName.trim()) {
      errors.companyName = "Company name is required.";
    } else if (data.companyName.trim().length < 2) {
      errors.companyName = "Company name must be at least 2 characters.";
    } else if (!isEnglishOnly(data.companyName)) {
      errors.companyName = "Please use English characters only (emojis and non-English scripts are not allowed).";
    }

    if (!data.aboutCompany || !data.aboutCompany.trim()) {
      errors.aboutCompany = "About company description is required.";
    } else if (data.aboutCompany.trim().length < 10) {
      errors.aboutCompany = "About company description must be at least 10 characters.";
    } else if (!isEnglishOnly(data.aboutCompany)) {
      errors.aboutCompany = "Please use English characters only (emojis and non-English scripts are not allowed).";
    }

    if (!data.phone || !data.phone.trim()) {
      errors.phone = "Contact number is required.";
    } else if (!/^\d{10,12}$/.test(data.phone.trim())) {
      errors.phone = "Contact number must be 10, 11 or 12 digits.";
    }

    if (!data.email || !data.email.trim()) {
      errors.email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) {
      errors.email = "Please enter a valid email address.";
    }

    if (!data.address || !data.address.trim()) {
      errors.address = "Address is required.";
    } else if (!isEnglishOnly(data.address)) {
      errors.address = "Please use English characters only (emojis and non-English scripts are not allowed).";
    }

    if (!data.area || !data.area.trim()) {
      errors.area = "Area is required.";
    } else if (!isEnglishOnly(data.area)) {
      errors.area = "Please use English characters only (emojis and non-English scripts are not allowed).";
    }

    if (!data.state || !data.state.trim()) {
      errors.state = "State is required.";
    } else {
      const validState = (apiStates || []).find(s => s && typeof s === 'string' && s.toLowerCase() === (data.state || '').trim().toLowerCase());
      if (!validState) {
        errors.state = "Please select a valid State from the list.";
      }
    }

    if (!data.city || !data.city.trim()) {
      errors.city = "City is required.";
    } else {
      const validCity = (apiCities || []).find(c => c && typeof c === 'string' && c.toLowerCase() === (data.city || '').trim().toLowerCase());
      if (!validCity) {
        errors.city = "Please select a valid City from the list.";
      }
    }

    if (!data.zipcode || !data.zipcode.trim()) {
      errors.zipcode = "Zipcode is required.";
    } else if (!/^\d{6}$/.test(data.zipcode.trim())) {
      errors.zipcode = "Zipcode must be exactly 6 digits.";
    }

    if (data.website && data.website.trim() && !isEnglishOnly(data.website)) {
      errors.website = "Please use English characters only (emojis and non-English scripts are not allowed).";
    }

    if (data.gstin && data.gstin.trim()) {
      if (data.gstin.trim().length !== 15) {
        errors.gstin = "GSTIN must be exactly 15 characters.";
      } else if (!isEnglishOnly(data.gstin)) {
        errors.gstin = "Please use English characters only (emojis and non-English scripts are not allowed).";
      }
    }

    if (data.instagram && data.instagram.trim() && !isEnglishOnly(data.instagram)) {
      errors.instagram = "Please use English characters only (emojis and non-English scripts are not allowed).";
    }
    if (data.linkedin && data.linkedin.trim() && !isEnglishOnly(data.linkedin)) {
      errors.linkedin = "Please use English characters only (emojis and non-English scripts are not allowed).";
    }
    if (data.facebook && data.facebook.trim() && !isEnglishOnly(data.facebook)) {
      errors.facebook = "Please use English characters only (emojis and non-English scripts are not allowed).";
    }
    if (data.twitter && data.twitter.trim() && !isEnglishOnly(data.twitter)) {
      errors.twitter = "Please use English characters only (emojis and non-English scripts are not allowed).";
    }

    if (!cat) {
      errors.category = "Category is required.";
    }

    if (cat && (!tags || tags.length === 0)) {
      errors.tags = "Please select at least one Tag for your category.";
    }

    if (!isEdit && !banner) {
      errors.profileBanner = "Profile Banner image is required.";
    }

    if (!logo) {
      errors.brandLogo = "Brand Logo image is required.";
    } else if (typeof logo !== 'string' && logo.name) {
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'webp'];
      const ext = logo.name.split('.').pop().toLowerCase();
      const allowedMimes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
      if (!allowedExtensions.includes(ext) || (logo.type && !allowedMimes.includes(logo.type))) {
        errors.brandLogo = "Brand Logo must be a valid PNG, JPG, or WEBP image.";
      }
    }

    if (!govtProof) {
      errors.govtProof = "Legal Document (GST, COI, MSME) is required.";
    } else if (typeof govtProof !== 'string' && govtProof.name) {
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'pdf'];
      const ext = govtProof.name.split('.').pop().toLowerCase();
      const allowedMimes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'application/pdf'];
      if (!allowedExtensions.includes(ext) || (govtProof.type && !allowedMimes.includes(govtProof.type))) {
        errors.govtProof = "Legal Document must be a valid PNG, JPG, JPEG, WEBP, or PDF file.";
      }
    }

    return errors;
  };

  const fieldErrors = validate(formData, selectedCategory, selectedTags, profileBanner, brandLogo, govtProof, !!editingListingId);

  const getInputClass = (fieldName, isTextArea = false) => {
    const hasError = isSubmitted && fieldErrors[fieldName];
    const heightClass = isTextArea ? "" : " h-[38px] md:h-[40px]";
    if (hasError) {
      return "w-full border border-red-400 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-400 bg-white transition-all shadow-2xs text-gray-800" + heightClass;
    }
    return "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 focus:border-[#2ab391] bg-white transition-all shadow-xs text-gray-750" + heightClass;
  };

  const handleBlur = () => {
    // Errors are displayed on submit click
  };


  const fetchCitiesForState = async (stateName) => {
    if (!stateName) {
      setApiCities([]);
      return;
    }
    setLoadingCities(true);
    try {
      const cities = await getCleanCitiesForState(stateName);
      setApiCities(cities);
      setApiError('');
    } catch (err) {
      console.error(`Failed to fetch cities for ${stateName}:`, err);
    } finally {
      setLoadingCities(false);
    }
  };

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const statesList = await getCleanStates();
        setApiStates(statesList);
        setApiError('');
      } catch (err) {
        console.error("Failed to fetch states:", err);
      }
    };
    fetchStates();

    const handleClickOutside = (event) => {
      if (yearRef.current && !yearRef.current.contains(event.target)) {
        setShowYearDropdown(false);
      }
      if (stateRef.current && !stateRef.current.contains(event.target)) {
        setShowStateDropdown(false);
      }
      if (cityRef.current && !cityRef.current.contains(event.target)) {
        setShowCityDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleStateSelect = (selectedState) => {
    setFormData(prev => ({ ...prev, state: selectedState, city: '' }));
    setShowStateDropdown(false);
    fetchCitiesForState(selectedState);
    setTouched(prev => ({ ...prev, state: true, city: false }));
  };

  const fetchPendingAdminDraft = useCallback(async () => {
    try {
      console.log("[ManageListing] Fetching pending admin drafts...");
      const res = await vendorService.getMyPendingAdminDraft();
      console.log("[ManageListing] Pending admin draft response:", res);
      if (res && res.success && res.data && res.data.hasDraft && res.data.draft) {
        console.log("[ManageListing] Found active draft:", res.data.draft);
        setPendingAdminDraft(res.data.draft);
        if (res.data.draft.listingType) {
          const draftListingType = res.data.draft.listingType.toUpperCase();
          setListingType(draftListingType);
          const catRes = await vendorService.getCategories(false, draftListingType);
          if (catRes.success) setCategories(catRes.data || []);
        }
      } else {
        setPendingAdminDraft(null);
      }
    } catch (err) {
      console.error("[ManageListing] Error loading pending admin draft:", err);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      // Load own listings first to determine listingType
      const listRes = await vendorService.getMyListings();
      let ownListings = [];
      let activeListingType = listingType;
      if (listRes.success) {
        ownListings = listRes.data.listings || [];
        setMyListings(ownListings);
        if (ownListings.length > 0) {
          activeListingType = (ownListings[0].listingType || 'COMPANY').toUpperCase();
          setListingType(activeListingType);
          setSelectedCategory(ownListings[0].categoryId?.toString() || '');
          setIsFormOpen(false); // Default to Close Mode on page load as requested
        }
      }

      // Load categories matching activeListingType
      const catRes = await vendorService.getCategories(false, activeListingType);
      if (catRes.success) setCategories(catRes.data || []);

      // Fetch membership status
      const memberRes = await vendorService.getCurrentMembership();
      if (memberRes.success) {
        setMembershipPlan(memberRes.data.plan_name || 'FREE');
        setMembershipStatus(memberRes.data.status || 'ACTIVE');
      }

      // Fetch user profile for default claimant name & phone
      let userEmail = '';
      let userPhone = '';
      try {
        const storedUser = authStorage.getItem('user');
        if (storedUser) {
          const userObj = JSON.parse(storedUser);
          userEmail = userObj.emailId || userObj.email || '';
          if (userEmail) {
            const profileRes = await authService.getUserProfile(userEmail);
            if (profileRes.success && profileRes.data) {
              const uData = profileRes.data;
              setUserProfile(uData);
              setClaimantName(uData.name || '');
              setClaimantPhone(uData.mobileNo || '');
              userPhone = uData.mobileNo || '';

              // Pre-populate listing details (name, state, city, phone, email) from registered user profile if creating a new listing
              setFormData(prev => ({
                ...prev,
                companyName: prev.companyName || (activeListingType === 'INDIVIDUAL' ? (uData.name || '') : ''),
                state: prev.state || uData.state || '',
                city: prev.city || uData.city || '',
                phone: prev.phone || uData.mobileNo || '',
                email: prev.email || uData.emailId || userEmail || ''
              }));
              if (uData.state) {
                fetchCitiesForState(uData.state);
              }
            }
          }
        }
      } catch (err) {
        console.error("Error fetching user profile for pre-populating claimant:", err);
      }

      // If user does not own any listings, evaluate claim scenario
      if (ownListings.length === 0) {
        setCheckingExisting(true);
        try {
          // Fetch user's claims first
          const claimsRes = await vendorService.getMyClaims();
          let myClaims = [];
          if (claimsRes.success && claimsRes.data) {
            myClaims = claimsRes.data;
            setUserClaims(myClaims);
          }

          if (claimListingId) {
            const targetId = parseInt(claimListingId, 10);

            // Find if a claim already exists for this listing
            const matchedClaim = myClaims.find(c => c.listing_id === targetId);

            // Fetch the target listing details
            const listingRes = await vendorService.getPublicListingById(targetId);
            if (listingRes.success && listingRes.data) {
              const targetListing = listingRes.data;

              if (targetListing.isClaimed || targetListing.created_by) {
                toast.warning("This business listing has already been claimed by another owner.");
              } else {
                setMatchedListing(targetListing);
                if (matchedClaim) {
                  setPendingClaim(matchedClaim);
                } else {
                  setPendingClaim(null);
                  setShowClaimModal(true); // Auto-open claim popup modal for new claims
                }
              }
            } else {
              toast.error("Failed to load details of the listing you are trying to claim.");
            }
          } else if (myClaims.length > 0) {
            // User has already claimed a listing, load it!
            const activeClaim = myClaims[0]; // Take the first claim
            const listingRes = await vendorService.getPublicListingById(activeClaim.listing_id);
            if (listingRes.success && listingRes.data) {
              setMatchedListing(listingRes.data);
              setPendingClaim(activeClaim);
            }
          } else if (userEmail) {
            // Standard unclaimed listing matching based on registered user profile details
            const matchRes = await vendorService.checkExistingListing(userEmail, userPhone);
            if (matchRes.success && matchRes.data && matchRes.data.found) {
              setMatchedListing(matchRes.data.listing);
              setPendingClaim(matchRes.data.pendingClaim);
            }
          }
        } catch (e) {
          console.error("Error processing claim matching on mount:", e);
        } finally {
          setCheckingExisting(false);
        }
      }

      // Load pending admin drafts
      await fetchPendingAdminDraft();

      setLoading(false);
    };
    fetchData();
  }, [claimListingId]);

  const handleApproveAdminDraft = async () => {
    if (!pendingAdminDraft) return;
    setSubmittingDecision(true);
    try {
      const res = await vendorService.respondToAdminDraft(pendingAdminDraft.id, 'APPROVE');
      if (res && res.success) {
        toast.success("Listing draft successfully approved! Your listing details are now live.");
        setPendingAdminDraft(null);
        setShowDraftPreviewModal(false);
        const listRes = await vendorService.getMyListings();
        if (listRes.success) setMyListings(listRes.data.listings || []);
        setMatchedListing(res.data);
        window.dispatchEvent(new Event('listingChanged'));
        window.location.reload();
      } else {
        toast.error(res.error || "Failed to approve listing changes.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to approve listing changes.");
    } finally {
      setSubmittingDecision(false);
    }
  };

  const handleRejectAdminDraft = async () => {
    if (!pendingAdminDraft) return;
    if (!draftRejectReason.trim() || draftRejectReason.trim().length < 10) {
      toast.error("Please provide a rejection reason (minimum 10 characters).");
      return;
    }
    setSubmittingDecision(true);
    try {
      const res = await vendorService.respondToAdminDraft(pendingAdminDraft.id, 'REJECT', draftRejectReason.trim());
      if (res && res.success) {
        toast.success("Listing draft rejected. Changes discarded.");
        setPendingAdminDraft(null);
        setShowRejectModal(false);
        setDraftRejectReason('');
      } else {
        toast.error(res.error || "Failed to reject listing draft.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to reject listing draft.");
    } finally {
      setSubmittingDecision(false);
    }
  };

  const handleConfirmDeleteListing = async () => {
    if (!deletingListing) return;
    if (deleteConfirmInput.trim().toUpperCase() !== 'DELETE') {
      toast.error("Please type DELETE to confirm permanent deletion.");
      return;
    }
    setDeletingInProgress(true);
    try {
      const res = await vendorService.deleteMyListing(deletingListing.id);
      if (res && res.success) {
        toast.success("Listing and all associated media files permanently deleted successfully.");
        setMyListings([]);
        setDeletingListing(null);
        setDeleteConfirmInput('');
        setShowDeleteStep2Modal(false);
        setShowDeleteStep1Modal(false);
        window.dispatchEvent(new Event('listingChanged'));
      } else {
        toast.error(res.error || "Failed to delete listing.");
      }
    } catch (err) {
      console.error("Error deleting listing:", err);
      toast.error(err.response?.data?.detail || "Failed to delete listing.");
    } finally {
      setDeletingInProgress(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTagToggle = (tagId) => {
    setSelectedTags(prev => {
      const nextTags = prev.includes(tagId) ? prev.filter(t => t !== tagId) : [...prev, tagId];
      setTouched(prevTouched => ({ ...prevTouched, tags: true }));
      return nextTags;
    });
  };

  const handleBrandToggle = (brandId) => {
    setSelectedBrands(prev => {
      const nextBrands = prev.includes(brandId) ? prev.filter(b => b !== brandId) : [...prev, brandId];
      return nextBrands;
    });
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setSelectedTags([]);
    setTagSearch('');
    setSelectedBrands([]);
    setBrandSearch('');
    setTouched(prev => ({ ...prev, category: true, tags: false }));
  };

  const activeCategory = categories.find(c => c.id.toString() === selectedCategory);
  const activeTags = activeCategory ? activeCategory.tags : [];
  const activeBrands = activeCategory ? (activeCategory.brands || []) : [];

  // Fetch file sizes of existing gallery URL strings via HTTP HEAD requests (with robust blob fallback)
  const fetchExistingGallerySizes = async (urls) => {
    if (!urls || urls.length === 0) return;
    setFetchingGallerySizes(true);
    const sizes = {};
    await Promise.allSettled(
      urls.map(async (url) => {
        try {
          const fullUrl = getImageUrl(url);
          // Try lightweight HEAD request first
          const res = await fetch(fullUrl, { method: 'HEAD' });
          let len = parseInt(res.headers.get('content-length') || '0', 10);
          // If HEAD returns 0 or null (e.g. CORS header not exposed in older sessions), fetch blob as fallback
          if (!len || isNaN(len) || len === 0) {
            const getRes = await fetch(fullUrl);
            if (getRes.ok) {
              const blob = await getRes.blob();
              len = blob.size;
            }
          }
          sizes[url] = isNaN(len) ? 0 : len;
        } catch (err) {
          console.error('[Gallery] Size request failed for existing image:', { url, error: err?.message });
          sizes[url] = 0;
        }
      })
    );
    setGalleryUrlSizes(sizes);
    setFetchingGallerySizes(false);
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setCompressingBanner({ progress: 10, message: 'Reading image file...' });
    try {
      const compressed = await compressImageIfNeeded(file, 5 * 1024 * 1024, (p, msg) => {
        setCompressingBanner({ progress: p, message: msg });
      });
      setProfileBanner(compressed);
      setTouched(prev => ({ ...prev, profileBanner: true }));
    } catch (err) {
      if (err.message === 'FILE_TOO_LARGE') {
        toast.error('Image is too large to upload. Please select an image under 5MB.');
      } else {
        console.error('[MediaCompressor] Banner compression failed:', err);
        toast.warning('Could not auto-compress image. Please try a smaller file.');
      }
    } finally {
      setCompressingBanner(null);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setCompressingLogo({ progress: 10, message: 'Reading image file...' });
    try {
      const compressed = await compressImageIfNeeded(file, 5 * 1024 * 1024, (p, msg) => {
        setCompressingLogo({ progress: p, message: msg });
      });
      setBrandLogo(compressed);
      setTouched(prev => ({ ...prev, brandLogo: true }));
    } catch (err) {
      if (err.message === 'FILE_TOO_LARGE') {
        toast.error('Image is too large to upload. Please select an image under 5MB.');
      } else {
        console.error('[MediaCompressor] Logo compression failed:', err);
        toast.warning('Could not auto-compress image. Please try a smaller file.');
      }
    } finally {
      setCompressingLogo(null);
    }
  };

  const handleGovtProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const allowedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'pdf'];
    const ext = file.name.split('.').pop().toLowerCase();
    const allowedMimes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'application/pdf'];

    if (!allowedExtensions.includes(ext) || (file.type && !allowedMimes.includes(file.type))) {
      toast.error('Invalid document format. Please upload a PNG, JPG, JPEG, WEBP, or PDF file.');
      return;
    }

    // PDFs cannot be compressed — reject if > 5MB
    if (isPDF(file)) {
      if (file.size > 5 * 1024 * 1024) {
        console.warn('[Upload] PDF too large — rejected:', { name: file.name, size: formatFileSize(file.size) });
        toast.error('PDF files must be under 5MB. Please compress it manually and try again.');
        return;
      }
      setGovtProof(file);
      setTouched(prev => ({ ...prev, govtProof: true }));
      return;
    }

    // Images — auto-compress if needed
    setCompressingGovtProof({ progress: 10, message: 'Reading document image...' });
    try {
      const compressed = await compressImageIfNeeded(file, 5 * 1024 * 1024, (p, msg) => {
        setCompressingGovtProof({ progress: p, message: msg });
      });
      setGovtProof(compressed);
      setTouched(prev => ({ ...prev, govtProof: true }));
    } catch (err) {
      if (err.message === 'FILE_TOO_LARGE') {
        toast.error('Image is too large to upload. Please select an image under 5MB.');
      } else {
        console.error('[MediaCompressor] GovtProof compression failed:', err);
        toast.warning('Could not auto-compress image. Please try a smaller file.');
      }
    } finally {
      setCompressingGovtProof(null);
    }
  };

  const MAX_GALLERY_SIZE = 15 * 1024 * 1024; // 15 MB total gallery limit
  const MIN_QUALITY_BUDGET = 300 * 1024; // 300 KB quality guardrail

  const handleGalleryUpload = async (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    if (compressingGallery) return; // Prevent concurrent uploads
    const rawFiles = Array.from(e.target.files);
    e.target.value = ''; // Reset input immediately

    // Compute current gallery total size
    const currentExistingSize = galleryImages
      .filter(img => typeof img === 'string')
      .reduce((sum, url) => sum + (galleryUrlSizes[url] || 0), 0);
    const currentNewFilesSize = galleryImages
      .filter(img => img instanceof File)
      .reduce((sum, f) => sum + f.size, 0);
    const currentTotal = currentExistingSize + currentNewFilesSize;
    const remainingBudget = MAX_GALLERY_SIZE - currentTotal;

    // Quality guardrail check: if available space is less than 300 KB
    if (remainingBudget < MIN_QUALITY_BUDGET) {
      toast.error(
        remainingBudget > 0
          ? `Gallery is almost full (only ${formatFileSize(remainingBudget)} remaining). Please remove an older image to upload new photos.`
          : 'Gallery is full! Total gallery size limit of 15 MB has been reached.'
      );
      return;
    }

    const numIncoming = rawFiles.length;
    const perImageBudget = Math.floor(remainingBudget / numIncoming);

    if (perImageBudget < MIN_QUALITY_BUDGET) {
      const maxAllowedPhotos = Math.floor(remainingBudget / MIN_QUALITY_BUDGET);
      toast.error(
        `Not enough space to upload ${numIncoming} images (${formatFileSize(remainingBudget)} remaining). You can add at most ${maxAllowedPhotos} photo${maxAllowedPhotos === 1 ? '' : 's'}, or delete older images.`
      );
      return;
    }

    // Adaptive per-image target budget: min(5MB, perImageBudget)
    const targetMaxPerImage = Math.min(5 * 1024 * 1024, perImageBudget);

    setCompressingGallery({ progress: 10, message: 'Optimizing images to fit gallery...' });

    // Compress incoming images adaptively to fit within remaining gallery budget
    let compressedFiles;
    try {
      compressedFiles = await compressManyImagesIfNeeded(rawFiles, targetMaxPerImage, (p, msg) => {
        setCompressingGallery({ progress: p, message: msg });
      });
    } catch (err) {
      if (err.message === 'FILE_TOO_LARGE') {
        toast.error('One or more images could not be optimized to fit the remaining gallery space. Please select smaller images or clear space.');
      } else {
        console.error('[MediaCompressor] Gallery compression failed:', err);
        toast.error('Failed to process images. Please try again.');
      }
      setCompressingGallery(null);
      return;
    }

    // Final verification against 15 MB cap
    const incomingSize = compressedFiles.reduce((sum, f) => sum + f.size, 0);
    if (currentTotal + incomingSize > MAX_GALLERY_SIZE) {
      toast.error(`Total gallery size cannot exceed 15 MB. (${formatFileSize(remainingBudget)} available).`);
      setCompressingGallery(null);
      return;
    }

    setGalleryImages(prev => [...prev, ...compressedFiles]);
    setCompressingGallery(null);
  };

  const removeGalleryImage = (index) => {
    setGalleryImages(prev => {
      const removed = prev[index];
      // If removing an existing URL, clear it from the size map
      if (typeof removed === 'string') {
        setGalleryUrlSizes(prevSizes => {
          const next = { ...prevSizes };
          delete next[removed];
          return next;
        });
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const addCustomHour = () => {
    if (businessHours.customHours.length >= 7) {
      toast.warning("You can add a maximum of 7 custom business hour rows.");
      return;
    }
    setBusinessHours(prev => ({
      ...prev,
      customHours: [...prev.customHours, { day: 'Day', openTime: '09:00 AM', closeTime: '06:00 PM' }]
    }));
  };

  const updateCustomHour = (index, field, value) => {
    setBusinessHours(prev => {
      const newCustom = [...prev.customHours];
      newCustom[index][field] = value;
      return { ...prev, customHours: newCustom };
    });
  };

  const removeCustomHour = (index) => {
    setBusinessHours(prev => ({
      ...prev,
      customHours: prev.customHours.filter((_, i) => i !== index)
    }));
  };

  const handleEdit = async (listing) => {
    setEditingListingId(listing.id);
    let lType = (listing.listingType || listing.listing_type || 'COMPANY').toUpperCase();

    const catRes = await vendorService.getCategories(false, lType);
    let fetchedCats = [];
    if (catRes.success && catRes.data) {
      fetchedCats = catRes.data;
      setCategories(fetchedCats);
      const matchedCat = fetchedCats.find(c => c.id.toString() === listing.categoryId?.toString());
      if (matchedCat && (matchedCat.categoryType || matchedCat.category_type)) {
        lType = (matchedCat.categoryType || matchedCat.category_type).toUpperCase();
      }
    }

    // Double check: if category not in current list, check Individual categories
    if (listing.categoryId && !fetchedCats.some(c => c.id.toString() === listing.categoryId?.toString())) {
      const indCatRes = await vendorService.getCategories(false, 'INDIVIDUAL');
      if (indCatRes.success && indCatRes.data) {
        const matchedInd = indCatRes.data.find(c => c.id.toString() === listing.categoryId?.toString());
        if (matchedInd) {
          lType = 'INDIVIDUAL';
          setCategories(indCatRes.data);
        }
      }
    }
    setListingType(lType);

    setTouched({
      companyName: false,
      aboutCompany: false,
      phone: false,
      email: false,
      address: false,
      state: false,
      city: false,
      zipcode: false,
      gstin: false,
      category: false,
      tags: false,
      profileBanner: false
    });

    const estDate = listing.establishedSince || '';

    setFormData({
      companyName: listing.companyName || '',
      parentCompanyName: listing.parentCompanyName || '',
      aboutCompany: listing.aboutCompany || '',
      phone: listing.phone || '',
      email: listing.email || '',
      address: listing.address || '',
      area: listing.area || '',
      state: listing.state || '',
      city: listing.city || '',
      zipcode: listing.zipcode || '',
      website: listing.website || '',
      gstin: listing.gstin || '',
      establishedSince: estDate,
      instagram: listing.instagram || '',
      linkedin: listing.linkedin || '',
      facebook: listing.facebook || '',
      twitter: listing.twitter || ''
    });
    setSelectedCategory(listing.categoryId?.toString() || '');
    setSelectedTags(listing.tags?.map(t => t.id) || []);
    setSelectedBrands(listing.brands?.map(b => b.id) || []);

    if (listing.businessHours) {
      setBusinessHoursType(listing.businessHours.type);
      const bh = listing.businessHours;
      setBusinessHours({
        mainHours: bh.mainHours ? {
          openTime: formatTimeForFrontend(bh.mainHours.openTime),
          closeTime: formatTimeForFrontend(bh.mainHours.closeTime)
        } : { openTime: '09:00 AM', closeTime: '06:00 PM' },
        saturdayHours: bh.saturdayHours ? {
          openTime: formatTimeForFrontend(bh.saturdayHours.openTime),
          closeTime: formatTimeForFrontend(bh.saturdayHours.closeTime)
        } : { openTime: 'Select Time', closeTime: 'Select Time' },
        sundayHours: bh.sundayHours ? {
          openTime: formatTimeForFrontend(bh.sundayHours.openTime),
          closeTime: formatTimeForFrontend(bh.sundayHours.closeTime)
        } : { openTime: 'Select Time', closeTime: 'Select Time' },
        customHours: bh.customHours ? bh.customHours.map(ch => ({
          day: ch.day,
          openTime: formatTimeForFrontend(ch.openTime),
          closeTime: formatTimeForFrontend(ch.closeTime)
        })) : []
      });
    }
    if (listing.state) {
      fetchCitiesForState(listing.state);
    } else {
      setApiCities([]);
    }
    setProfileBanner(listing.profileBannerUrl || null);
    setBrandLogo(listing.logoUrl || null);
    setGovtProof(listing.govtProofUrl || null);
    setGalleryImages(listing.galleryImageUrls || []);
    setGalleryUrlSizes({});
    if (listing.galleryImageUrls && listing.galleryImageUrls.length > 0) {
      fetchExistingGallerySizes(listing.galleryImageUrls);
    }
    if (listing.businessHours || listing.instagram || listing.linkedin || listing.facebook || listing.twitter) {
      setShowAdditionalDetails(true);
    } else {
      setShowAdditionalDetails(false);
    }
    setIsFormOpen(true);
  };


  const resetForm = () => {
    setIsFormOpen(false);
    setShowAdditionalDetails(false);
    setEditingListingId(null);
    setIsSubmitted(false);
    setTouched({
      companyName: false,
      aboutCompany: false,
      phone: false,
      email: false,
      address: false,
      area: false,
      state: false,
      city: false,
      zipcode: false,
      gstin: false,
      category: false,
      tags: false,
      profileBanner: false,
      govtProof: false
    });
    setFormData({
      companyName: listingType === 'INDIVIDUAL' ? (userProfile?.name || '') : '',
      aboutCompany: '',
      phone: userProfile?.mobileNo || '',
      email: userProfile?.emailId || '',
      address: '',
      area: '',
      state: userProfile?.state || '',
      city: userProfile?.city || '',
      zipcode: '',
      website: '',
      gstin: '',
      establishedSince: '',
      instagram: '',
      linkedin: '',
      facebook: '',
      twitter: ''
    });
    setSelectedCategory('');
    setSelectedTags([]);
    setProfileBanner(null);
    setBrandLogo(null);
    setGovtProof(null);
    setGalleryImages([]);
    setGalleryUrlSizes({});
    setFetchingGallerySizes(false);
    setBusinessHours({
      mainHours: { openTime: '09:00 AM', closeTime: '06:00 PM' },
      saturdayHours: { openTime: 'Select Time', closeTime: 'Select Time' },
      sundayHours: { openTime: 'Select Time', closeTime: 'Select Time' },
      customHours: []
    });
    setBusinessHoursType('MON_TO_SAT');
    if (userProfile?.state) {
      fetchCitiesForState(userProfile.state);
    } else {
      setApiCities([]);
    }
    setSelectedBrands([]);
    setTagSearch('');
    setBrandSearch('');
    setShowYearDropdown(false);
    setShowStateDropdown(false);
    setShowCityDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);

    if (!editingListingId) {
      const hasApproved = userClaims.some(c => c.status === 'APPROVED');
      if (hasApproved) {
        toast.warning("You already have an approved claim. You cannot create a new listing.");
        return;
      }

      const hasPending = userClaims.some(c => c.status === 'PENDING');
      if (hasPending) {
        toast.warning("You already have a claim request pending approval. You cannot create a new listing until your current request is resolved.");
        return;
      }
    }

    setSubmitting(true);

    try {
      const errors = validate(formData, selectedCategory, selectedTags, profileBanner, brandLogo, govtProof, !!editingListingId);
      if (Object.keys(errors).length > 0) {
        toast.warning("Please correct the errors in the form.");
        setSubmitting(false);

        // Scroll to the first field with an error in visual order
        const fieldOrder = [
          'companyName',
          'gstin',
          'govtProof',
          'brandLogo',
          'profileBanner',
          'aboutCompany',
          'category',
          'tags',
          'phone',
          'email',
          'address',
          'state',
          'city',
          'area',
          'zipcode',
          'website',
          'instagram',
          'linkedin',
          'facebook',
          'twitter'
        ];

        const firstErrorField = fieldOrder.find(field => errors[field]);
        if (firstErrorField) {
          if (['instagram', 'linkedin', 'facebook', 'twitter'].includes(firstErrorField)) {
            setShowAdditionalDetails(true);
          }
          setTimeout(() => {
            const element = document.getElementsByName(firstErrorField)[0];
            if (element) {
              if (element.type === 'file' || element.classList.contains('hidden')) {
                const parent = element.parentElement || element;
                parent.scrollIntoView({ behavior: 'smooth', block: 'center' });
              } else {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.focus();
              }
            }
          }, 50);
        }
        return;
      }

      const validState = (apiStates || []).find(s => s && typeof s === 'string' && s.toLowerCase() === (formData.state || '').trim().toLowerCase());
      const validCity = (apiCities || []).find(c => c && typeof c === 'string' && c.toLowerCase() === (formData.city || '').trim().toLowerCase());

      // Normalize form data values
      formData.state = validState;
      formData.city = validCity;

      if (businessHoursType === 'CUSTOM') {
        const validCustomHours = businessHours.customHours.filter(ch => ch.day && ch.day !== 'Day');

        if (validCustomHours.length === 0) {
          toast.warning("For Custom Business Hours, at least one day is required.");
          setSubmitting(false);
          return;
        }

        if (validCustomHours.length > 7) {
          toast.warning("For Custom Business Hours, a maximum of 7 days is allowed.");
          setSubmitting(false);
          return;
        }

        const uniqueDays = new Set(validCustomHours.map(ch => ch.day));
        if (uniqueDays.size !== validCustomHours.length) {
          toast.warning("Each day in Custom Business Hours must be unique. Duplicate days are not allowed.");
          setSubmitting(false);
          return;
        }
      }

      const estYear = formData.establishedSince ? parseInt(formData.establishedSince, 10) : null;

      const listingDto = {
        ...formData,
        listingType: listingType,
        establishedSince: estYear,
        categoryId: parseInt(selectedCategory),
        tagIds: selectedTags,
        brandIds: selectedBrands,
        galleryImageUrls: galleryImages.filter(img => typeof img === 'string'),
        businessHours: {
          type: businessHoursType,
          mainHours: (businessHoursType === 'MON_TO_SAT' || businessHoursType === 'MON_TO_FRI')
            ? {
              openTime: formatTimeForBackend(businessHours.mainHours.openTime),
              closeTime: formatTimeForBackend(businessHours.mainHours.closeTime)
            }
            : null,
          saturdayHours: businessHoursType === 'MON_TO_FRI'
            ? {
              openTime: formatTimeForBackend(businessHours.saturdayHours.openTime),
              closeTime: formatTimeForBackend(businessHours.saturdayHours.closeTime)
            }
            : null,
          sundayHours: (businessHoursType === 'MON_TO_SAT' || businessHoursType === 'MON_TO_FRI')
            ? {
              openTime: formatTimeForBackend(businessHours.sundayHours.openTime),
              closeTime: formatTimeForBackend(businessHours.sundayHours.closeTime)
            }
            : null,
          customHours: businessHoursType === 'CUSTOM' ? businessHours.customHours.filter(ch => ch.day && ch.day !== 'Day').map(ch => ({
            day: ch.day,
            openTime: formatTimeForBackend(ch.openTime),
            closeTime: formatTimeForBackend(ch.closeTime)
          })) : null
        }
      };

      const submitData = new FormData();
      submitData.append('listing', new Blob([JSON.stringify(listingDto)], { type: 'application/json' }));

      if (profileBanner && typeof profileBanner !== 'string') {
        submitData.append('profileBanner', profileBanner);
      }

      if (brandLogo && typeof brandLogo !== 'string') {
        submitData.append('logo', brandLogo);
      }

      if (govtProof && typeof govtProof !== 'string') {
        submitData.append('govtProof', govtProof);
      }

      galleryImages.forEach(img => {
        if (typeof img !== 'string') {
          submitData.append('galleryImages', img);
        }
      });

      const response = editingListingId
        ? await vendorService.updateListing(editingListingId, submitData)
        : await vendorService.createListing(submitData);

      if (response.success) {
        if (editingListingId) {
          const isApproved = response.data && response.data.status === 'APPROVED';
          if (isApproved) {
            toast.success("Listing updated successfully!");
          } else {
            toast.success("Listing updated successfully! Admin approval is required before the updates go live.");
          }
        } else {
          toast.success("Listing created successfully! Admin approval is required.");
        }
        resetForm();

        const listRes = await vendorService.getMyListings();
        if (listRes.success) setMyListings(listRes.data.listings || []);
        window.dispatchEvent(new Event('listingChanged'));
      } else {
        toast.error("Failed: " + response.error);
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred during submission.");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredStates = (apiStates || []).filter(s =>
    s && typeof s === 'string' && s.toLowerCase().includes((formData.state || '').toLowerCase())
  );

  const filteredCities = (apiCities || []).filter(c =>
    c && typeof c === 'string' && c.toLowerCase().includes((formData.city || '').toLowerCase())
  );

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading...</div>;
  }

  return (
    <div className="w-full">
      {pendingAdminDraft && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm animate-in fade-in duration-200 text-left">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-bold text-amber-900">Action Required: Listing Review</h3>
              <p className="text-xs text-amber-700 mt-0.5">
                An administrator ({pendingAdminDraft.adminEmail}) has submitted a listing <strong>{pendingAdminDraft.actionType === 'CREATE' ? 'creation/registration' : 'update'}</strong> for your review.
              </p>
              <p className="text-[10px] text-amber-500 mt-1">Submitted on: {pendingAdminDraft.createdAt}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 self-end md:self-center">
            <button
              type="button"
              onClick={() => setShowDraftPreviewModal(true)}
              className="px-3.5 py-1.5 bg-white border border-amber-200 rounded-lg text-xs font-bold text-amber-800 hover:bg-amber-100/50 flex items-center gap-1 transition"
            >
              <Eye className="w-3.5 h-3.5" /> Preview Proposed Details
            </button>
            <button
              type="button"
              onClick={() => setShowApproveConfirmModal(true)}
              disabled={submittingDecision}
              className="px-3.5 py-1.5 bg-[#2ab391] hover:bg-[#22977a] text-white rounded-lg text-xs font-bold shadow-sm flex items-center gap-1 transition disabled:opacity-50"
            >
              Approve & Publish Live
            </button>
            <button
              type="button"
              onClick={() => setShowRejectConfirmModal(true)}
              disabled={submittingDecision}
              className="px-3.5 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold shadow-sm flex items-center gap-1 transition disabled:opacity-50"
            >
              Reject
            </button>
          </div>
        </div>
      )}

      {/* Draft Preview Modal */}
      {showDraftPreviewModal && pendingAdminDraft && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl border border-slate-100 max-w-3xl w-full max-h-[85vh] flex flex-col my-8 animate-in fade-in zoom-in-95 duration-200">

            <div className="p-4 bg-slate-100 border-b border-slate-200 font-bold text-slate-800 flex justify-between items-center shrink-0">
              <span className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#2ab391]" />
                Preview Proposed Listing Details
              </span>
              <button type="button" onClick={() => setShowDraftPreviewModal(false)} className="p-1 hover:bg-slate-200 rounded">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6 text-sm text-left">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="block text-[10px] uppercase font-bold text-slate-400">
                    {pendingAdminDraft.listingType === 'INDIVIDUAL' ? 'Name' : 'Company Name'}
                  </span>
                  <span className="text-slate-800 font-semibold">{pendingAdminDraft.companyName}</span>
                </div>
                <div>
                  <span className="block text-[10px] uppercase font-bold text-slate-400">Phone Number</span>
                  <span className="text-slate-800 font-semibold">{pendingAdminDraft.phone || 'N/A'}</span>
                </div>
                <div>
                  <span className="block text-[10px] uppercase font-bold text-slate-400">Email</span>
                  <span className="text-slate-800 font-semibold">{pendingAdminDraft.email || 'N/A'}</span>
                </div>
                <div>
                  <span className="block text-[10px] uppercase font-bold text-slate-400">GSTIN</span>
                  <span className="text-slate-800 font-semibold">{pendingAdminDraft.gstin || 'N/A'}</span>
                </div>
                <div className="md:col-span-2">
                  <span className="block text-[10px] uppercase font-bold text-slate-400">
                    {pendingAdminDraft.listingType === 'INDIVIDUAL' ? 'About Professional / Service' : 'About Company'}
                  </span>
                  <p className="text-slate-600 mt-1 whitespace-pre-line bg-slate-50 p-3 rounded-lg border border-slate-200">{pendingAdminDraft.aboutCompany || 'N/A'}</p>
                </div>
                <div className="md:col-span-2">
                  <span className="block text-[10px] uppercase font-bold text-slate-400">Location</span>
                  <span className="text-slate-800 font-semibold">
                    {[
                      pendingAdminDraft.address,
                      pendingAdminDraft.city,
                      pendingAdminDraft.state ? `${pendingAdminDraft.state}${pendingAdminDraft.zipcode ? ` - ${pendingAdminDraft.zipcode}` : ''}` : null
                    ].filter(Boolean).join(', ')}
                  </span>
                </div>
                {pendingAdminDraft.area && (
                  <div>
                    <span className="block text-[10px] uppercase font-bold text-slate-400">Area</span>
                    <span className="text-slate-800 font-semibold">{pendingAdminDraft.area}</span>
                  </div>
                )}
                {pendingAdminDraft.website && (
                  <div>
                    <span className="block text-[10px] uppercase font-bold text-slate-400">Website</span>
                    <a href={pendingAdminDraft.website} target="_blank" rel="noreferrer" className="text-[#2ab391] hover:underline font-semibold">{pendingAdminDraft.website}</a>
                  </div>
                )}
                {pendingAdminDraft.establishedSince && (
                  <div>
                    <span className="block text-[10px] uppercase font-bold text-slate-400">Established Since</span>
                    <span className="text-slate-800 font-semibold">{pendingAdminDraft.establishedSince}</span>
                  </div>
                )}
                <div>
                  <span className="block text-[10px] uppercase font-bold text-slate-400">Category</span>
                  <span className="text-slate-800 font-semibold">{pendingAdminDraft.categoryName || 'N/A'}</span>
                </div>
              </div>

              {/* Tags & Brands */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-100 pt-4">
                <div>
                  <span className="block text-[10px] uppercase font-bold text-slate-400 mb-1.5">Selected Tags</span>
                  <div className="flex flex-wrap gap-1.5">
                    {pendingAdminDraft.tags?.length === 0 ? (
                      <span className="text-xs text-slate-400">None</span>
                    ) : (
                      pendingAdminDraft.tags?.map(t => (
                        <span key={t.id} className="text-xs px-2 py-0.5 bg-[#2ab391]/10 text-[#2ab391] rounded-full font-medium border border-[#2ab391]/20">{t.name}</span>
                      ))
                    )}
                  </div>
                </div>
                <div>
                  <span className="block text-[10px] uppercase font-bold text-slate-400 mb-1.5">Selected Brands</span>
                  <div className="flex flex-wrap gap-1.5">
                    {pendingAdminDraft.brands?.length === 0 ? (
                      <span className="text-xs text-slate-400">None</span>
                    ) : (
                      pendingAdminDraft.brands?.map(b => (
                        <span key={b.id} className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full font-medium border border-indigo-100">{b.name}</span>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Social Links */}
              {(pendingAdminDraft.instagram || pendingAdminDraft.linkedin || pendingAdminDraft.facebook || pendingAdminDraft.twitter) && (
                <div className="border-t border-slate-100 pt-4 grid grid-cols-2 gap-4">
                  {pendingAdminDraft.instagram && (
                    <div>
                      <span className="block text-[10px] uppercase font-bold text-slate-400">Instagram</span>
                      <span className="text-xs text-slate-600">{pendingAdminDraft.instagram}</span>
                    </div>
                  )}
                  {pendingAdminDraft.linkedin && (
                    <div>
                      <span className="block text-[10px] uppercase font-bold text-slate-400">LinkedIn</span>
                      <span className="text-xs text-slate-600">{pendingAdminDraft.linkedin}</span>
                    </div>
                  )}
                  {pendingAdminDraft.facebook && (
                    <div>
                      <span className="block text-[10px] uppercase font-bold text-slate-400">Facebook</span>
                      <span className="text-xs text-slate-600">{pendingAdminDraft.facebook}</span>
                    </div>
                  )}
                  {pendingAdminDraft.twitter && (
                    <div>
                      <span className="block text-[10px] uppercase font-bold text-slate-400">Twitter / X</span>
                      <span className="text-xs text-slate-600">{pendingAdminDraft.twitter}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Business Hours */}
              {pendingAdminDraft.businessHours && (
                <div className="border-t border-slate-100 pt-4">
                  <span className="block text-[10px] uppercase font-bold text-slate-400 mb-2">Business Hours</span>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 space-y-1">
                    <p className="font-semibold text-slate-700">Type: {pendingAdminDraft.businessHours.type?.replace(/_/g, ' ')}</p>
                    {pendingAdminDraft.businessHours.mainHours && (
                      <p>Mon - Fri/Sat: {pendingAdminDraft.businessHours.mainHours.openTime} to {pendingAdminDraft.businessHours.mainHours.closeTime}</p>
                    )}
                    {pendingAdminDraft.businessHours.saturdayHours && (
                      <p>Saturday: {pendingAdminDraft.businessHours.saturdayHours.openTime} to {pendingAdminDraft.businessHours.saturdayHours.closeTime}</p>
                    )}
                    {pendingAdminDraft.businessHours.sundayHours && (
                      <p>Sunday: {pendingAdminDraft.businessHours.sundayHours.openTime} to {pendingAdminDraft.businessHours.sundayHours.closeTime}</p>
                    )}
                    {pendingAdminDraft.businessHours.customHours?.map((row, idx) => (
                      <p key={idx}>{row.day}: {row.openTime} to {row.closeTime}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Media Section */}
              <div className="border-t border-slate-100 pt-4 space-y-4">
                <span className="block text-[10px] uppercase font-bold text-slate-400">Media Attachments</span>
                <div className="grid grid-cols-3 gap-4">
                  {pendingAdminDraft.profileBannerUrl && (
                    <div>
                      <span className="block text-[9px] uppercase font-bold text-slate-400 mb-1">Profile Banner</span>
                      <img src={getImageUrl(pendingAdminDraft.profileBannerUrl)} alt="Draft banner" className="w-full h-16 object-cover rounded border" />
                    </div>
                  )}
                  {pendingAdminDraft.logoUrl && (
                    <div>
                      <span className="block text-[9px] uppercase font-bold text-slate-400 mb-1 text-center">Company Logo</span>
                      <img src={getImageUrl(pendingAdminDraft.logoUrl)} alt="Draft logo" className="w-12 h-12 object-cover rounded-full border mx-auto" />
                    </div>
                  )}
                  {pendingAdminDraft.govtProofUrl && (
                    <div>
                      <span className="block text-[9px] uppercase font-bold text-slate-400 mb-1">Govt Proof</span>
                      <a href={getImageUrl(pendingAdminDraft.govtProofUrl)} target="_blank" rel="noreferrer" className="text-[#2ab391] hover:underline font-bold text-xs">View Document</a>
                    </div>
                  )}
                </div>

                {pendingAdminDraft.galleryImageUrls?.length > 0 && (
                  <div>
                    <span className="block text-[9px] uppercase font-bold text-slate-400 mb-1">Gallery Images</span>
                    <div className="flex flex-wrap gap-2">
                      {pendingAdminDraft.galleryImageUrls.map((url, idx) => (
                        <img key={idx} src={getImageUrl(url)} alt="Draft gallery" className="w-14 h-14 object-cover rounded border" />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setShowDraftPreviewModal(false)}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-100 text-center"
              >
                Close Preview
              </button>
              <button
                type="button"
                onClick={() => setShowRejectConfirmModal(true)}
                disabled={submittingDecision}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-lg text-sm transition shadow-sm text-center"
              >
                Reject Draft
              </button>
              <button
                type="button"
                onClick={() => setShowApproveConfirmModal(true)}
                disabled={submittingDecision}
                className="px-5 py-2 bg-[#2ab391] hover:bg-[#239c7e] text-white font-bold rounded-lg text-sm transition shadow-sm text-center"
              >
                Approve & Publish Live
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Approve Draft Confirmation Modal */}
      {showApproveConfirmModal && pendingAdminDraft && (
        <div className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-xl border border-slate-100 max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-150 text-left">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#2ab391]" />
              Approve Proposed Details
            </h3>
            <p className="text-sm text-slate-600 mt-2">
              Are you sure you want to approve this listing draft?
            </p>
            <p className="text-xs text-emerald-600 mt-2 bg-emerald-50/50 p-2 border border-emerald-100/40 rounded-lg">
              Once approved, these changes will apply immediately, update your public profile, and go LIVE on the directory.
            </p>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowApproveConfirmModal(false)}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50"
              >
                Go Back
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowApproveConfirmModal(false);
                  handleApproveAdminDraft();
                }}
                disabled={submittingDecision}
                className="px-5 py-2 bg-[#2ab391] hover:bg-[#239c7e] text-white font-bold rounded-lg text-sm transition shadow-sm"
              >
                Yes, Approve & Publish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Draft Confirmation Modal */}
      {showRejectConfirmModal && pendingAdminDraft && (
        <div className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-xl border border-slate-100 max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-150 text-left">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-rose-500" />
              Reject Proposed Changes
            </h3>
            <p className="text-sm text-slate-600 mt-2">
              Are you sure you want to reject this administrative draft?
            </p>
            <p className="text-xs text-rose-600 mt-2 bg-rose-50 p-2 border border-rose-100 rounded-lg">
              Rejecting this draft will discard all proposed profile modifications. You will be prompted to provide a brief rejection reason for the administrator.
            </p>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowRejectConfirmModal(false)}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50"
              >
                Go Back
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowRejectConfirmModal(false);
                  setShowRejectModal(true);
                }}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-lg text-sm transition shadow-sm"
              >
                Yes, Reject Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Reason Modal */}
      {showRejectModal && pendingAdminDraft && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-xl border border-slate-100 max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-150 text-left">
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-rose-500" />
              Reject Admin Draft
            </h3>
            <p className="text-sm text-slate-600 mt-2">
              Please let the admin know why you are rejecting the proposed changes.
            </p>
            <div className="mt-4">
              <textarea
                rows={3}
                required
                placeholder="Provide a reason for rejection (minimum 10 characters)..."
                value={draftRejectReason}
                onChange={(e) => setDraftRejectReason(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 bg-white"
              />
              <p className="text-[10px] text-slate-400 mt-1">Rejection reasons help admins correct mistakes and re-submit drafts.</p>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowRejectModal(false);
                  setDraftRejectReason('');
                }}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRejectAdminDraft}
                disabled={submittingDecision || !draftRejectReason.trim() || draftRejectReason.trim().length < 10}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold rounded-lg text-sm transition shadow-sm"
              >
                Submit Rejection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deletion Step 1 Modal */}
      {showDeleteStep1Modal && deletingListing && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 max-w-md w-full p-5 sm:p-6 animate-in fade-in zoom-in-95 duration-150 text-left">
            <div className="flex items-center gap-3 text-rose-600 mb-3">
              <div className="w-10 h-10 rounded-full bg-rose-50 border border-rose-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-rose-600" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-slate-800">
                Delete Listing Submission?
              </h3>
            </div>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
              Are you sure you want to delete your unapproved listing submission for <strong>"{deletingListing.companyName}"</strong>?
            </p>
            <p className="text-xs text-amber-800 bg-amber-50/80 p-3 rounded-xl border border-amber-200/60 mt-3 font-medium">
              Since this listing has not been approved by an administrator yet, you can delete it now and create a new listing if needed.
            </p>

            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2.5 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteStep1Modal(false);
                  setDeletingListing(null);
                }}
                className="px-4 py-2 border border-slate-200 rounded-xl text-xs sm:text-sm font-semibold hover:bg-slate-50 transition text-center cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowDeleteStep1Modal(false);
                  setShowDeleteStep2Modal(true);
                }}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-xl text-xs sm:text-sm transition shadow-sm flex items-center justify-center gap-1.5 cursor-pointer"
              >
                Next: Confirm Deletion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deletion Step 2 Modal - Permanent Deletion Warning */}
      {showDeleteStep2Modal && deletingListing && (
        <div className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-4 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 max-w-md w-full p-5 sm:p-6 animate-in fade-in zoom-in-95 duration-150 text-left">
            <div className="flex items-center gap-3 text-rose-600 mb-3">
              <div className="w-10 h-10 rounded-full bg-rose-100 border border-rose-200 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-rose-600 animate-pulse" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-slate-900">
                  Confirm Permanent Deletion
                </h3>
                <p className="text-[11px] text-rose-600 font-bold uppercase tracking-wider">Step 2 of 2 — Final Warning</p>
              </div>
            </div>

            <div className="p-3.5 bg-rose-50 border border-rose-200/80 rounded-xl text-xs text-rose-900 space-y-2 mt-2">
              <strong className="block font-bold text-rose-950 flex items-center gap-1">
                <AlertCircle size={14} className="text-rose-600 shrink-0" />
                PERMANENT ACTION WARNING:
              </strong>
              <p className="leading-relaxed text-rose-800">
                This action <strong>CANNOT be undone</strong>. Deleting this listing will permanently remove:
              </p>
              <ul className="list-disc list-inside space-y-1 text-rose-800 text-[11px] font-semibold pl-1">
                <li>Listing database profile & history</li>
                <li>Uploaded logo, profile banner & legal documents</li>
                <li>All uploaded gallery photos & attachments</li>
              </ul>
            </div>

            <div className="mt-4 space-y-1.5">
              <label className="block text-xs font-bold text-slate-700">
                To confirm permanent deletion, please type <span className="text-rose-600 font-extrabold">DELETE</span> below:
              </label>
              <input
                type="text"
                value={deleteConfirmInput}
                onChange={(e) => setDeleteConfirmInput(e.target.value)}
                placeholder='Type "DELETE"'
                className="w-full border border-slate-300 rounded-xl px-3 py-2 text-xs sm:text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-500 uppercase tracking-wider"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2.5 mt-6">
              <button
                type="button"
                disabled={deletingInProgress}
                onClick={() => {
                  setShowDeleteStep2Modal(false);
                  setShowDeleteStep1Modal(true);
                  setDeleteConfirmInput('');
                }}
                className="px-4 py-2 border border-slate-200 rounded-xl text-xs sm:text-sm font-semibold hover:bg-slate-50 transition text-center cursor-pointer"
              >
                Go Back
              </button>
              <button
                type="button"
                disabled={deletingInProgress || deleteConfirmInput.trim().toUpperCase() !== 'DELETE'}
                onClick={handleConfirmDeleteListing}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold rounded-xl text-xs sm:text-sm transition shadow-md shadow-rose-600/20 flex items-center justify-center gap-1.5 cursor-pointer"
              >
                {deletingInProgress ? 'Deleting Media & Data...' : 'Permanently Delete Listing'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 border-b pb-4 space-y-3">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">Manage Listing</h2>
        </div>
        {myListings.length === 0 && !matchedListing && userProfile?.listingDeletedNotice && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200/80 rounded-xl p-3.5 sm:p-4 text-xs md:text-sm text-red-700 shadow-xs animate-in fade-in duration-200">
            <AlertCircle className="text-red-500 flex-shrink-0" size={18} />
            <span className="font-semibold font-sans">
              As per your requirement, your listing has been deleted successfully. You can create a new listing.
            </span>
          </div>
        )}
        {myListings.length === 0 && !matchedListing && !userProfile?.listingDeletedNotice && (
          <div className="flex items-center gap-3 bg-sky-50/40 border border-sky-100 rounded-xl p-4 text-xs md:text-sm text-sky-900">
            <AlertCircle className="text-[#2ab391] flex-shrink-0" size={18} />
            <span className="font-semibold font-sans">
              To receive inquiries from architects and designers, list your business here.
            </span>
          </div>
        )}
      </div>

      {/* Top Listing Type Selection Bar */}
      {myListings.length > 0 || editingListingId ? (
        <div className="flex items-center gap-2 mb-5 text-xs sm:text-sm font-semibold">
          <span className="text-gray-500">Listing Type:</span>
          <span className="text-[#2ab391] font-bold">
            {(myListings[0]?.listing_type === 'INDIVIDUAL' || listingType === 'INDIVIDUAL') ? 'Individual Business' : 'Company'}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-2.5 sm:gap-3 mb-5 text-xs sm:text-sm font-semibold flex-wrap">
          <span className="text-gray-700 font-bold">Listing Type :</span>
          <select
            value={listingType}
            onChange={(e) => {
              const val = e.target.value;
              setListingType(val);
              setSelectedCategory('');
              setSelectedTags([]);
              setSelectedBrands([]);
              if (!editingListingId) {
                if (val === 'INDIVIDUAL') {
                  setFormData(prev => ({
                    ...prev,
                    companyName: prev.companyName || userProfile?.name || ''
                  }));
                } else {
                  setFormData(prev => ({
                    ...prev,
                    companyName: (prev.companyName === userProfile?.name) ? '' : prev.companyName
                  }));
                }
              }
              vendorService.getCategories(false, val).then(res => {
                if (res.success) setCategories(res.data || []);
              });
            }}
            className="bg-white border border-gray-300 rounded-xl px-3 py-1.5 text-xs sm:text-sm font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#2ab391]/30 focus:border-[#2ab391] shadow-2xs cursor-pointer"
          >
            <option value="COMPANY">Company</option>
            <option value="INDIVIDUAL">Individual Business</option>
          </select>
        </div>
      )}

      {!isFormOpen ? (
        <div className="flex flex-col gap-4">
          {myListings.length === 0 ? (
            matchedListing ? (
              <div className="w-full max-w-xl mx-auto flex flex-col gap-4">
                {/* Highlighted option to Create new listing from scratch at the TOP */}
                {(!pendingClaim || pendingClaim.status !== 'PENDING') && (
                  <div className="flex justify-between items-center bg-teal-50/50 border border-teal-100/40 rounded-xl p-3 flex-wrap gap-2 mb-1">
                    <span className="text-xs text-gray-550 font-semibold">Not your business?</span>
                    <button
                      type="button"
                      onClick={() => {
                        setMatchedListing(null);
                        setPendingClaim(null);
                        setClaimComments('');
                        setClaimProofFile(null);
                        setClaimCommentsError('');
                        setClaimProofFileError('');
                        setSearchParams({});
                      }}
                      className="inline-flex items-center justify-center px-3.5 py-1.5 text-xs font-bold text-[#2ab391] bg-white hover:bg-teal-50 border border-[#2ab391]/20 rounded-lg transition-colors duration-150 cursor-pointer shadow-2xs"
                    >
                      Create new listing from scratch
                    </button>
                  </div>
                )}

                <div className="bg-white border border-gray-200 rounded-lg p-5 flex flex-col gap-4 text-left shadow-sm relative">
                  {/* Title & Status Badge */}
                  <div className="flex justify-between items-start">
                    <h3 className="font-bold text-[#1f2937] text-lg inline-flex items-center gap-2 flex-wrap">
                      {matchedListing.companyName}
                      {pendingClaim && pendingClaim.status === 'PENDING' && (
                        <span className="px-2.5 py-0.5 text-xs font-bold rounded-full uppercase bg-orange-50 text-orange-700 border border-orange-200">
                          Claim Pending
                        </span>
                      )}
                      {pendingClaim && pendingClaim.status === 'REJECTED' && (
                        <span className="px-2.5 py-0.5 text-xs font-bold rounded-full uppercase bg-red-50 text-red-700 border border-red-200">
                          Claim Rejected
                        </span>
                      )}
                      {!pendingClaim && (
                        <span className="px-2.5 py-0.5 text-xs font-bold rounded-full uppercase bg-teal-50 text-[#2ab391] border border-teal-200">
                          Claim Suggestion
                        </span>
                      )}
                    </h3>
                  </div>

                  {/* Category */}
                  <div className="text-sm text-[#64748b] font-medium -mt-2">
                    {matchedListing.categoryName || 'General Listing'}
                  </div>

                  {/* Contact & Location details */}
                  <div className="space-y-1.5 text-xs text-gray-550 font-semibold pt-1">
                    <p>📞 Phone: <span className="text-gray-700">{matchedListing.phone || 'N/A'}</span></p>
                    <p>✉️ Email: <span className="text-gray-700">{matchedListing.email || 'N/A'}</span></p>
                    <p>📍 Location: <span className="text-gray-700">{matchedListing.address ? `${matchedListing.address}, ` : ''}{matchedListing.city ? `${matchedListing.city}, ` : ''}{matchedListing.state}</span></p>
                  </div>

                  {/* Rejection Alert Box */}
                  {pendingClaim && pendingClaim.status === 'REJECTED' && (
                    <div className="p-4 bg-red-50/60 border border-red-100 text-red-800 rounded-xl text-xs leading-relaxed mt-1">
                      <strong className="block text-red-950 font-bold mb-1">Reason for rejection:</strong>
                      <p className="font-semibold bg-white border border-red-200/50 p-3 rounded-lg text-gray-750 italic">
                        "{pendingClaim.reject_reason || 'No specific explanation provided.'}"
                      </p>
                      <p className="text-[10px] text-red-655 pt-1.5 font-bold">
                        Please review the reasons above and use the button below to update your documents and submission details.
                      </p>
                    </div>
                  )}

                  {/* Claim Submission summary details for pending */}
                  {pendingClaim && pendingClaim.status === 'PENDING' && (
                    <div className="p-4 bg-orange-50/50 border border-orange-100 text-orange-850 rounded-xl text-xs leading-relaxed mt-1 space-y-2">
                      <strong className="block text-orange-950 font-bold">Submission Details Under Review:</strong>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-gray-750">
                        <p><strong>Claimant Name:</strong> {pendingClaim.claimantName || 'N/A'}</p>
                        <p><strong>Contact Phone:</strong> {pendingClaim.claimantPhone || 'N/A'}</p>
                        <p><strong>Designation:</strong> {pendingClaim.claimantDesignation || 'N/A'}</p>
                      </div>
                      {pendingClaim.comments && (
                        <p className="pt-1.5 border-t border-orange-100 text-gray-700"><strong>Comments:</strong> <span className="italic">"{pendingClaim.comments}"</span></p>
                      )}
                      {pendingClaim.proofPhotoUrl && (
                        <div className="pt-2 border-t border-orange-100">
                          <a
                            href={getImageUrl(pendingClaim.proofPhotoUrl)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[#2ab391] hover:text-[#23977a] hover:underline flex items-center gap-1 font-bold"
                          >
                            <Eye size={14} /> View Proof Document
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Claim Suggestion Description */}
                  {!pendingClaim && (
                    <div className="p-4 bg-teal-50/50 border border-teal-100 text-teal-800 rounded-xl text-xs leading-relaxed font-semibold mt-1">
                      We found an existing unclaimed listing in our database matching your details. You can claim ownership of this listing to verify your business, manage customer reviews, and update content!
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex flex-col gap-3.5 pt-2">
                    {pendingClaim && pendingClaim.status === 'REJECTED' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setClaimantName(pendingClaim.claimantName || '');
                          setClaimantPhone(pendingClaim.claimantPhone || '');
                          setClaimantDesignation(pendingClaim.claimantDesignation || '');
                          setClaimComments(pendingClaim.comments || '');
                          setClaimProofFile(null);
                          setShowClaimModal(true);
                        }}
                        className="w-full bg-[#2ab391] hover:bg-[#23977a] text-white font-bold text-sm px-6 py-2.5 rounded-lg shadow-sm transition-all duration-150 flex items-center justify-center gap-1.5 cursor-pointer active:scale-98"
                      >
                        Update Claim Details
                      </button>
                    ) : pendingClaim && pendingClaim.status === 'PENDING' ? (
                      null
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setShowClaimModal(true);
                        }}
                        className="w-full bg-[#2ab391] hover:bg-[#23977a] text-white font-bold text-sm px-6 py-2.5 rounded-lg shadow-sm transition-all duration-150 flex items-center justify-center gap-1.5 cursor-pointer active:scale-98"
                      >
                        Claim Business Ownership
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative border border-slate-100 rounded-2xl p-8 flex flex-col items-center justify-center text-center bg-gradient-to-br from-white to-slate-50 shadow-sm overflow-hidden min-h-[300px] w-full">
                <div className="absolute inset-0 opacity-10 filter blur-[2px] pointer-events-none select-none flex flex-col justify-around p-6">
                  <div className="border border-slate-300 rounded p-3 w-3/4 self-start"></div>
                  <div className="border border-slate-300 rounded p-3 w-1/2 self-end"></div>
                </div>

                <div className="relative z-10 flex flex-col items-center max-w-md">
                  <button
                    type="button"
                    onClick={() => {
                      if (!editingListingId) resetForm();
                      setIsFormOpen(true);
                    }}
                    className="w-16 h-16 rounded-full bg-[#eaf7f4] hover:bg-[#d5f0eb] flex items-center justify-center text-[#2ab391] mb-4 shadow-inner cursor-pointer transition-colors"
                    title="Create Profile"
                  >
                    <Plus size={32} />
                  </button>
                  <h3 className="font-bold text-gray-800 text-lg mb-2">
                    No Listing Created Yet
                  </h3>
                  <p className="text-gray-550 text-sm mb-6 leading-relaxed">
                    Create your listing and connect to grow your business with Top Interiors' network of professional services.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      if (!editingListingId) resetForm();
                      setIsFormOpen(true);
                    }}
                    className="bg-[#2ab391] hover:bg-[#23977a] text-white font-medium text-sm px-6 py-2.5 rounded-lg shadow-sm transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0"
                  >
                    Create Your Listing
                  </button>
                </div>
              </div>
            )
          ) : (
            myListings.map(listing => {
              const parseDate = (val) => {
                if (!val) return "May 15, 2026";
                if (Array.isArray(val)) {
                  return new Date(val[0], val[1] - 1, val[2]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                }
                // Match DD-MM-YYYY HH:MM:SS AM/PM (or single digits)
                const match = String(val).match(/^(\d{1,2})-(\d{1,2})-(\d{4})\s+(\d{1,2}):(\d{1,2}):(\d{1,2})\s*(AM|PM)?$/i);
                let d;
                if (match) {
                  const day = parseInt(match[1], 10);
                  const month = parseInt(match[2], 10) - 1; // 0-indexed month
                  const year = parseInt(match[3], 10);
                  let hours = parseInt(match[4], 10);
                  const minutes = parseInt(match[5], 10);
                  const seconds = parseInt(match[6], 10);
                  const ampm = match[7];

                  if (ampm) {
                    if (ampm.toUpperCase() === 'PM' && hours < 12) {
                      hours += 12;
                    } else if (ampm.toUpperCase() === 'AM' && hours === 12) {
                      hours = 0;
                    }
                  }
                  d = new Date(year, month, day, hours, minutes, seconds);
                } else {
                  d = new Date(val);
                }
                return isNaN(d.getTime()) ? "May 15, 2026" : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
              };
              const postDate = parseDate(listing.createdAt);

              return (
                <div key={listing.id} className="bg-white border border-gray-200/90 rounded-2xl p-4 sm:p-5 flex flex-col gap-3 relative shadow-xs hover:border-gray-300 transition-all">
                  {/* Top Row: Title, Status Badge & Update Icon Button */}
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-gray-900 text-base sm:text-lg">
                          {listing.companyName}
                        </h3>
                        <span className={`px-2.5 py-0.5 text-[11px] font-bold rounded-full uppercase tracking-wider ${
                          listing.status === 'APPROVED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                          listing.status === 'PENDING' ? 'bg-orange-50 text-orange-700 border border-orange-200' :
                          listing.status === 'SUSPENDED' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                          listing.status === 'REJECTED' ? 'bg-red-50 text-red-700 border border-red-200' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {listing.status}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-500 font-medium mt-1">
                        <Calendar size={14} className="text-gray-400" />
                        <span>Posted {postDate}</span>
                      </div>
                    </div>

                    {/* Action Icon Buttons */}
                    {(() => {
                      const canDelete = !listing.hasBeenApproved && !listing.isUpdated && listing.status === 'PENDING';
                      return (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleEdit(listing)}
                            title="Update Listing"
                            aria-label="Update Listing"
                            className="p-2 text-[#2ab391] hover:text-[#239b7d] hover:bg-teal-50/80 rounded-lg transition-all duration-150 flex items-center justify-center cursor-pointer active:scale-95"
                          >
                            <Edit3 size={18} />
                          </button>
                          {canDelete && (
                            <button
                              type="button"
                              onClick={() => {
                                setDeletingListing(listing);
                                setShowDeleteStep1Modal(true);
                              }}
                              title="Delete Listing"
                              aria-label="Delete Listing"
                              className="p-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition-all duration-150 flex items-center justify-center cursor-pointer active:scale-95"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Category Name - plain text without border or box */}
                  <div className="text-xs sm:text-sm text-gray-700 font-medium">
                    {listing.categoryName || 'Uncategorized'}
                  </div>

                  {/* View Count */}
                  <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-600 font-medium pt-0.5">
                    <Eye size={16} className="text-gray-400" />
                    <span>View Count : <strong className="text-gray-800 font-bold">{listing.viewCount || 0}</strong></span>
                  </div>

                  {/* Profile Completeness Section */}
                  {(() => {
                    const completeness = calculateListingCompleteness(
                      listing,
                      (listing.listingType || listing.listing_type || '').toUpperCase() === 'INDIVIDUAL'
                    );

                    return (
                      <div className="mt-2.5 pt-2.5 border-t border-gray-100 flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${completeness.iconBg}`}>
                              <Sparkles size={13} />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="text-xs sm:text-sm font-bold text-gray-800">Profile Completeness</span>
                                <span className={`px-1.5 py-0.2 text-[11px] font-bold rounded-md border ${completeness.badgeColor}`}>
                                  {completeness.percentage}%
                                </span>
                              </div>
                              <p className="text-[11px] text-gray-400 font-normal leading-normal">
                                {completeness.percentage >= 100
                                  ? 'Your profile is 100% complete and fully optimized for discovery!'
                                  : 'Complete your profile to get up to 3x more client enquiries.'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden relative">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ease-out ${completeness.progressColor}`}
                            style={{ width: `${Math.max(4, completeness.percentage)}%` }}
                          />
                        </div>

                        {/* Actionable Missing Field Chips */}
                        {completeness.percentage < 100 && completeness.missingItems.length > 0 && (
                          <div className="flex flex-col gap-1.5 pt-0.5">
                            <span className="text-[11px] font-medium text-gray-500">Quick additions to boost your reach:</span>
                            <div className="flex flex-wrap gap-1.5">
                              {completeness.missingItems.slice(0, 6).map((item) => (
                                <button
                                  key={item.id}
                                  type="button"
                                  onClick={() => handleQuickJumpToField(listing, item)}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 hover:border-[#2ab391]/50 transition-all duration-150 cursor-pointer active:scale-95 shadow-2xs group"
                                  title={`Click to fill ${item.label}`}
                                >
                                  <span className={`font-bold text-[11px] shrink-0 ${completeness.chipPointColor}`}>+{item.points}%</span>
                                  <span className="text-gray-700 font-medium">{item.label}</span>
                                </button>
                              ))}
                              {completeness.missingItems.length > 6 && (
                                <button
                                  type="button"
                                  onClick={() => handleEdit(listing)}
                                  className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium text-gray-500 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-all cursor-pointer"
                                >
                                  +{completeness.missingItems.length - 6} more
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Rejected Reason Banner */}
                  {listing.status === 'REJECTED' && listing.rejectReason && (
                    <div className="mt-2 p-4 bg-red-50/70 border border-red-100 text-red-800 rounded-xl text-xs leading-relaxed">
                      <strong className="block text-red-950 mb-1 font-bold text-sm">Listing Rejected by Admin:</strong>
                      <p className="font-medium text-gray-750">{listing.rejectReason}</p>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="space-y-5 md:space-y-6">
          {listingType === 'INDIVIDUAL' ? (
            /* Individual Business Form Layout */
            <div className="space-y-6 bg-white border border-gray-200 rounded-2xl p-6 shadow-xs">
              <h3 className="text-[#2ab391] font-bold text-base tracking-wide border-b border-gray-100 pb-3 flex items-center gap-2">
                01 <span className="text-gray-800 font-bold">Individual Business Details</span>
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 1. Name */}
                <div className="space-y-1">
                  <label className={LABEL_CLASS}>Name<span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Enter your full name / business title"
                    className={getInputClass('companyName')}
                  />
                  {isSubmitted && fieldErrors.companyName && (
                    <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                      <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                      {fieldErrors.companyName}
                    </p>
                  )}
                </div>

                {/* 2. Contact no. */}
                <div className="space-y-1">
                  <label className={LABEL_CLASS}>Contact No.<span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 12);
                      setFormData(prev => ({ ...prev, phone: val }));
                    }}
                    onBlur={handleBlur}
                    placeholder="Enter 10-12 digit phone number"
                    className={getInputClass('phone')}
                  />
                  {isSubmitted && fieldErrors.phone && (
                    <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                      <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                      {fieldErrors.phone}
                    </p>
                  )}
                </div>

                {/* 3. Email id */}
                <div className="space-y-1">
                  <label className={LABEL_CLASS}>Email ID<span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Enter email address"
                    className={getInputClass('email')}
                  />
                  {isSubmitted && fieldErrors.email && (
                    <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                      <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                      {fieldErrors.email}
                    </p>
                  )}
                </div>

                {/* 4. Category Dropdown (Individual Business Categories) */}
                <div className="space-y-1">
                  <label className={LABEL_CLASS}>Category<span className="text-red-500">*</span></label>
                  <select
                    name="category"
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                    }}
                    onBlur={handleBlur}
                    className={getInputClass('category')}
                  >
                    <option value="">Select Category</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  {isSubmitted && fieldErrors.category && (
                    <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                      <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                      {fieldErrors.category}
                    </p>
                  )}
                </div>

                {/* 5. State */}
                <div ref={stateRef} className="relative space-y-1">
                  <label className={LABEL_CLASS}>State<span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, state: e.target.value, city: '' }));
                        setShowStateDropdown(true);
                      }}
                      onFocus={() => setShowStateDropdown(true)}
                      onBlur={handleBlur}
                      placeholder="Select State"
                      className={getInputClass('state')}
                    />
                    <div
                      onClick={() => setShowStateDropdown(!showStateDropdown)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-400"
                    >
                      <ChevronDown size={18} />
                    </div>
                  </div>
                  {showStateDropdown && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {(apiStates || [])
                        .filter(s => s && typeof s === 'string' && s.toLowerCase().includes((formData.state || '').toLowerCase()))
                        .map(stateName => (
                          <div
                            key={stateName}
                            onClick={() => handleStateSelect(stateName)}
                            className="px-4 py-2 text-sm text-gray-700 hover:bg-[#eaf7f4] hover:text-[#2ab391] cursor-pointer"
                          >
                            {stateName}
                          </div>
                        ))}
                    </div>
                  )}
                  {isSubmitted && fieldErrors.state && (
                    <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                      <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                      {fieldErrors.state}
                    </p>
                  )}
                </div>

                {/* 6. City */}
                <div ref={cityRef} className="relative space-y-1">
                  <label className={LABEL_CLASS}>City<span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, city: e.target.value }));
                        setShowCityDropdown(true);
                      }}
                      onFocus={() => {
                        if (formData.state && apiCities.length === 0 && !loadingCities) {
                          fetchCitiesForState(formData.state);
                        }
                        setShowCityDropdown(true);
                      }}
                      onBlur={handleBlur}
                      disabled={!formData.state}
                      placeholder={!formData.state ? "Select State first" : "Select City"}
                      className={getInputClass('city')}
                    />
                    <div
                      onClick={() => {
                        if (formData.state) setShowCityDropdown(!showCityDropdown);
                      }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-400"
                    >
                      <ChevronDown size={18} />
                    </div>
                  </div>
                  {showCityDropdown && formData.state && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {loadingCities ? (
                        <div className="px-4 py-2 text-sm text-gray-400">Loading cities...</div>
                      ) : (apiCities || []).filter(c => c && typeof c === 'string' && c.toLowerCase().includes((formData.city || '').toLowerCase())).length > 0 ? (
                        (apiCities || [])
                          .filter(c => c && typeof c === 'string' && c.toLowerCase().includes((formData.city || '').toLowerCase()))
                          .map(cityName => (
                            <div
                              key={cityName}
                              onClick={() => {
                                setFormData(prev => ({ ...prev, city: cityName }));
                                setShowCityDropdown(false);
                              }}
                              className="px-4 py-2 text-sm text-gray-700 hover:bg-[#eaf7f4] hover:text-[#2ab391] cursor-pointer"
                            >
                              {cityName}
                            </div>
                          ))
                      ) : (
                        <div className="px-4 py-2 text-sm text-gray-400">No cities found</div>
                      )}
                    </div>
                  )}
                  {isSubmitted && fieldErrors.city && (
                    <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                      <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                      {fieldErrors.city}
                    </p>
                  )}
                </div>

                {/* 7. Area */}
                <div className="space-y-1">
                  <label className={LABEL_CLASS}>Area<span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="area"
                    value={formData.area}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Enter locality / area"
                    className={getInputClass('area')}
                  />
                  {isSubmitted && fieldErrors.area && (
                    <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                      <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                      {fieldErrors.area}
                    </p>
                  )}
                </div>

                {/* 8. Legal Documents (Aadhaar Card / PAN Card) */}
                <div className="space-y-1">
                  <label className={LABEL_CLASS}>Legal Documents (Aadhaar Card / PAN Card)<span className="text-red-500">*</span></label>
                  {compressingGovtProof ? (
                    <div className="border border-[#2ab391]/40 bg-teal-50/60 rounded-xl p-2.5 flex items-center justify-between min-h-[42px] select-none">
                      <div className="flex items-center gap-2 min-w-0">
                        <Loader size={14} className="animate-spin text-[#2ab391] shrink-0" />
                        <span className="text-xs font-bold text-gray-800 truncate">Optimizing Document ({compressingGovtProof.progress}%)...</span>
                      </div>
                      <div className="w-16 bg-gray-200 rounded-full h-1.5 overflow-hidden shrink-0 ml-2">
                        <div className="bg-[#2ab391] h-1.5 rounded-full transition-all duration-200" style={{ width: `${compressingGovtProof.progress}%` }} />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 sm:gap-2.5 min-h-[42px]">
                      <input
                        type="file"
                        ref={govtProofInputRef}
                        name="govtProof"
                        onChange={handleGovtProofUpload}
                        accept=".jpg,.jpeg,.png,.pdf"
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => govtProofInputRef.current?.click()}
                        className="h-[38px] sm:h-[42px] px-2.5 sm:px-4 bg-white border border-gray-200 hover:border-[#2ab391] text-gray-700 text-[11px] sm:text-xs md:text-sm font-semibold rounded-xl transition-all flex items-center gap-1.5 sm:gap-2 cursor-pointer whitespace-nowrap shadow-2xs shrink-0"
                      >
                        <Upload size={14} className="text-[#2ab391] flex-shrink-0" />
                        <span>{govtProof ? (typeof govtProof === 'string' ? 'Change Document' : govtProof.name) : 'Upload Aadhaar / PAN Card'}</span>
                      </button>
                      {govtProof && (
                        <span className="text-[11px] sm:text-xs md:text-sm text-emerald-600 font-semibold flex items-center gap-1 whitespace-nowrap shrink-0">
                          <Check size={14} className="flex-shrink-0" /> Document Uploaded
                        </span>
                      )}
                    </div>
                  )}
                  {isSubmitted && fieldErrors.govtProof && (
                    <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                      <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                      {fieldErrors.govtProof}
                    </p>
                  )}
                  <p className="text-[11px] text-red-500 font-normal leading-tight mt-1">
                    Incorrect or random documents may lead to permanent profile suspension.
                  </p>
                </div>

                {/* 9. Product & Service Photos (Image Gallery) - Full Width */}
                <div className="space-y-1 md:col-span-2">
                  <label className={LABEL_CLASS + " flex justify-between"}>
                    <span>Product &amp; Service Photos (Image Gallery)</span>
                    <span className="text-xs text-gray-400 font-medium normal-case tracking-normal">
                      {(() => {
                        const existSz = galleryImages.filter(img => typeof img === 'string').reduce((s, url) => s + (galleryUrlSizes[url] || 0), 0);
                        const newSz = galleryImages.filter(img => img instanceof File).reduce((s, f) => s + f.size, 0);
                        return `${formatFileSize(existSz + newSz)} / 15 MB used`;
                      })()}
                      {fetchingGallerySizes && <span className="ml-1 text-gray-300 text-[10px]">(loading...)</span>}
                    </span>
                  </label>

                  {membershipPlan === 'PREMIUM' && membershipStatus === 'ACTIVE' ? (
                    <div>
                      {compressingGallery ? (
                        <div className="border border-[#2ab391]/40 bg-teal-50/60 rounded-xl p-3 flex items-center justify-between h-16 select-none mb-2">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <Loader size={16} className="animate-spin text-[#2ab391] shrink-0" />
                            <div className="truncate">
                              <p className="text-xs font-bold text-gray-800">Optimizing Gallery Images...</p>
                              <p className="text-[10px] text-gray-500 font-medium truncate">{compressingGallery.message}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            <div className="w-16 sm:w-20 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                              <div className="bg-[#2ab391] h-1.5 rounded-full transition-all duration-200" style={{ width: `${compressingGallery.progress}%` }} />
                            </div>
                            <span className="text-xs font-bold text-[#2ab391] w-8 text-right">{compressingGallery.progress}%</span>
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={() => galleryInputRef.current?.click()}
                          className="border border-dashed border-gray-300 rounded-xl p-3 flex flex-col items-center justify-center h-20 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors mb-2"
                        >
                          <Upload className="text-[#2ab391] mb-1" size={18} />
                          <p className="text-xs font-semibold text-gray-700">Click to upload product / service photos</p>
                          <p className="text-[10px] text-gray-400">PNG, JPG, JPEG up to 15 MB total budget</p>
                        </div>
                      )}
                      <input type="file" ref={galleryInputRef} onChange={handleGalleryUpload} accept="image/*" multiple className="hidden" />

                      {galleryImages.length > 0 && (
                        <div className="flex gap-2 overflow-x-auto py-1">
                          {galleryImages.map((img, idx) => (
                            <div key={idx} className="relative w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200 group shadow-2xs">
                              <img
                                src={typeof img === 'string' ? getImageUrl(img) : URL.createObjectURL(img)}
                                alt={`gallery-${idx}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 24 24' fill='%23f1f5f9' stroke='%2394a3b8' stroke-width='1.5'><rect width='18' height='18' x='3' y='3' rx='2'/><circle cx='9' cy='9' r='2'/><path d='m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21'/></svg>";
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => removeGalleryImage(idx)}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-md p-1 opacity-90 hover:opacity-100 transition-opacity"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="border border-dashed border-gray-300 bg-gray-50 rounded-xl p-3 h-24 flex flex-col justify-center items-center text-center">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Lock size={15} className="text-gray-400" />
                        <h4 className="font-semibold text-gray-700 text-xs sm:text-sm">
                          {membershipStatus === 'EXPIRED' ? 'Your plan has expired' : 'Upgrade to upload gallery images'}
                        </h4>
                      </div>
                      <p className="text-[11px] text-gray-500 mb-2 leading-tight max-w-md">
                        {membershipStatus === 'EXPIRED'
                          ? 'Renew your membership to upload product/service images.'
                          : 'Product & Service Photos (Image Gallery) are available for Premium plan members.'}
                      </p>
                      <a
                        href="/my-account/upgrade-membership"
                        onClick={(e) => { e.preventDefault(); navigate('/my-account/upgrade-membership'); }}
                        className="bg-[#2ab391] text-white text-xs px-3.5 py-1 rounded-lg font-semibold hover:bg-[#23977a] transition-all shadow-xs"
                      >
                        {membershipStatus === 'EXPIRED' ? 'Renew Plan' : 'Upgrade to Premium'}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <hr className="border-gray-100" />

              {/* Additional Details Toggle Prompt for Individual Listing */}
              {!showAdditionalDetails ? (
                <div className="py-1">
                  <button
                    type="button"
                    onClick={() => setShowAdditionalDetails(true)}
                    className="text-[#2ab391] hover:text-[#219074] py-1 text-xs sm:text-sm font-semibold flex items-center gap-2 transition-colors cursor-pointer group focus:outline-none"
                  >
                    <Plus size={18} className="transition-transform group-hover:scale-110 flex-shrink-0" />
                    <span className="hover:underline text-left">Add Social Media Profiles</span>
                    <ChevronDown size={18} className="flex-shrink-0" />
                  </button>
                </div>
              ) : (
                /* Social Profile */
                <section className="space-y-3">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-[#2ab391] font-semibold text-sm tracking-wide">
                      <span className="text-gray-800 font-semibold">Social Profile</span>
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowAdditionalDetails(false)}
                      className="text-xs text-gray-400 hover:text-gray-600 font-medium flex items-center gap-1 cursor-pointer transition-colors"
                    >
                      <span>Hide Details</span>
                      <ChevronUp size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <label className={LABEL_CLASS}>Instagram</label>
                      <input type="text" name="instagram" value={formData.instagram} onChange={handleInputChange} placeholder="Enter instagram link" className={INPUT_CLASS} />
                    </div>
                    <div className="space-y-1">
                      <label className={LABEL_CLASS}>Facebook</label>
                      <input type="text" name="facebook" value={formData.facebook} onChange={handleInputChange} placeholder="Enter Facebook link" className={INPUT_CLASS} />
                    </div>
                    <div className="space-y-1">
                      <label className={LABEL_CLASS}>LinkedIn</label>
                      <input type="text" name="linkedin" value={formData.linkedin} onChange={handleInputChange} placeholder="Enter linkedin link" className={INPUT_CLASS} />
                    </div>
                    <div className="space-y-1">
                      <label className={LABEL_CLASS}>X</label>
                      <input type="text" name="twitter" value={formData.twitter} onChange={handleInputChange} placeholder="Enter X link" className={INPUT_CLASS} />
                    </div>
                  </div>
                </section>
              )}

              {/* Form Action Buttons for Individual Business */}
              <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-5 py-2.5 border border-gray-200 text-gray-700 rounded-xl text-xs md:text-sm font-bold hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2.5 bg-[#2ab391] hover:bg-[#23977a] disabled:opacity-50 text-white rounded-xl text-xs md:text-sm font-bold shadow-md shadow-[#2ab391]/20 transition-all cursor-pointer flex items-center gap-2 whitespace-nowrap"
                >
                  {submitting ? 'Submitting...' : editingListingId ? 'Update Listing' : 'Create Listing'}
                </button>
              </div>
            </div>
          ) : (
            /* Standard Company Form Layout */
            <>
              {/* 01 Company Details */}
              <section className="space-y-3">
                <h3 className="text-[#2ab391] font-semibold text-sm tracking-wide mb-2">01 <span className="text-gray-800 font-semibold ml-1">Company Details</span></h3>

                {/* Row 1: Company Name */}
                <div className="space-y-1">
                  <label className={LABEL_CLASS}>Company Name<span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="Company name"
                    className={getInputClass('companyName')}
                  />
                  {isSubmitted && fieldErrors.companyName && (
                    <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                      <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                      {fieldErrors.companyName}
                    </p>
                  )}
                </div>

                {/* Row 2: GSTIN & Estd Since (Single line on mobile & desktop) */}
                <div className="grid grid-cols-2 gap-3 sm:gap-3.5">
                  <div className="space-y-1 min-w-0">
                    <label className={LABEL_CLASS + " truncate"}>GSTIN <span className="text-emerald-600 font-normal text-[10px] sm:text-xs normal-case ml-0.5 font-sans hidden sm:inline">(Preferred for trust)</span></label>
                    <div className="relative">
                      <input
                        type="text"
                        name="gstin"
                        value={formData.gstin}
                        onChange={(e) => {
                          const val = e.target.value.slice(0, 15).toUpperCase();
                          setFormData(prev => ({ ...prev, gstin: val }));
                        }}
                        onBlur={handleBlur}
                        placeholder="Enter GSTIN (Optional)"
                        className={getInputClass('gstin') + " text-xs sm:text-sm"}
                      />
                    </div>
                    {isSubmitted && fieldErrors.gstin && (
                      <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                        <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                        {fieldErrors.gstin}
                      </p>
                    )}
                  </div>

                  <div ref={yearRef} className="relative space-y-1 min-w-0">
                    <label className={LABEL_CLASS}>Established Since</label>
                    <div className="relative">
                      <input
                        type="text"
                        name="establishedSince"
                        value={formData.establishedSince}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setFormData(prev => ({ ...prev, establishedSince: val }));
                        }}
                        onFocus={() => setShowYearDropdown(true)}
                        placeholder="Select or enter year"
                        className={INPUT_CLASS + " pr-8 text-xs sm:text-sm"}
                      />
                      <div
                        onClick={() => setShowYearDropdown(!showYearDropdown)}
                        className="absolute inset-y-0 right-0 pr-2.5 flex items-center cursor-pointer text-gray-400 hover:text-gray-600"
                      >
                        <ChevronDown size={16} />
                      </div>
                    </div>
                    {showYearDropdown && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {(() => {
                          const currentYear = new Date().getFullYear();
                          const years = [];
                          for (let i = 0; i < 100; i++) {
                            years.push(currentYear - i);
                          }
                          const filteredYears = formData.establishedSince
                            ? years.filter(yr => yr.toString().includes(formData.establishedSince.toString()))
                            : years;

                          return filteredYears.length > 0 ? (
                            filteredYears.map(yr => (
                              <div
                                key={yr}
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, establishedSince: yr.toString() }));
                                  setShowYearDropdown(false);
                                }}
                                className="px-3 py-2 text-xs sm:text-sm text-gray-700 hover:bg-[#eaf7f4] hover:text-[#2ab391] cursor-pointer transition-colors"
                              >
                                {yr}
                              </div>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-xs text-gray-400">No years found</div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Row 3: Legal Document & Parent / Registered Company Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-3.5 items-start">
                  {/* Left: Legal Document */}
                  <div className="space-y-1 min-w-0">
                    <label className={LABEL_CLASS}>Legal Document (GST, COI, MSME)<span className="text-red-500">*</span></label>
                    {compressingGovtProof ? (
                      <div className="border border-[#2ab391]/40 bg-teal-50/60 rounded-lg p-3 flex items-center justify-between h-[38px] md:h-[40px] select-none">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <Loader size={16} className="animate-spin text-[#2ab391] shrink-0" />
                          <div className="truncate">
                            <p className="text-xs font-bold text-gray-800">Optimizing Legal Document...</p>
                            <p className="text-[10px] text-gray-500 font-medium truncate">{compressingGovtProof.message}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-2">
                          <div className="w-14 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                            <div className="bg-[#2ab391] h-1.5 rounded-full transition-all duration-200" style={{ width: `${compressingGovtProof.progress}%` }} />
                          </div>
                          <span className="text-xs font-bold text-[#2ab391] w-8 text-right">{compressingGovtProof.progress}%</span>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`w-full border rounded-lg px-3 py-2 flex items-center justify-between gap-2 bg-white transition-all shadow-xs h-[38px] md:h-[40px] ${isSubmitted && fieldErrors.govtProof ? 'border-red-400 bg-red-50/10' : 'border-gray-300 hover:border-[#2ab391]'
                          }`}
                      >
                        {govtProof ? (
                          <div className="flex items-center justify-between w-full min-w-0">
                            <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                              <div className="w-6 h-6 rounded bg-[#eaf7f4] text-[#2ab391] flex items-center justify-center flex-shrink-0">
                                <ShieldCheck size={14} />
                              </div>
                              <span
                                className="text-xs font-normal text-gray-600 truncate min-w-0 flex-1"
                                title={typeof govtProof === 'string' ? (govtProof.split('?')[0].split('/').pop() || 'Legal_Document.pdf') : govtProof.name}
                              >
                                {typeof govtProof === 'string'
                                  ? (govtProof.split('?')[0].split('/').pop() || 'Legal_Document.pdf')
                                  : govtProof.name
                                }
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {typeof govtProof === 'string' ? (
                                <a
                                  href={getImageUrl(govtProof)}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-xs font-semibold text-[#2ab391] hover:text-[#23977a] px-1.5 py-0.5 rounded hover:bg-[#eaf7f4] transition-colors cursor-pointer"
                                >
                                  View
                                </a>
                              ) : (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const fileUrl = URL.createObjectURL(govtProof);
                                    window.open(fileUrl, '_blank');
                                  }}
                                  className="text-xs font-semibold text-[#2ab391] hover:text-[#23977a] px-1.5 py-0.5 rounded hover:bg-[#eaf7f4] transition-colors cursor-pointer"
                                >
                                  View
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  govtProofInputRef.current?.click();
                                }}
                                className="text-xs font-semibold text-[#2ab391] hover:text-[#23977a] px-1.5 py-0.5 rounded hover:bg-[#eaf7f4] transition-colors cursor-pointer"
                              >
                                Change
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setGovtProof(null);
                                  if (govtProofInputRef.current) govtProofInputRef.current.value = '';
                                }}
                                className="w-5 h-5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors cursor-pointer"
                                title="Remove document"
                              >
                                <X size={13} />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() => govtProofInputRef.current?.click()}
                            className="flex items-center justify-between w-full cursor-pointer min-w-0"
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <Upload className="text-[#2ab391] flex-shrink-0" size={15} />
                              <span className="text-xs text-gray-400 truncate">Upload GST, COI, MSME</span>
                            </div>
                            <span className="text-xs text-[#2ab391] font-semibold flex-shrink-0 ml-1">Browse</span>
                          </div>
                        )}
                      </div>
                    )}
                    {isSubmitted && fieldErrors.govtProof && (
                      <p className="text-xs text-red-500 font-normal flex items-center gap-1">
                        <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                        {fieldErrors.govtProof}
                      </p>
                    )}
                    <p className="text-[11px] text-red-500 font-normal leading-tight">
                      Incorrect or random documents may lead to permanent profile suspension.
                    </p>
                  </div>

                  {/* Right: Parent / Registered Company Name (Optional) */}
                  <div className="space-y-1 min-w-0">
                    <label className={LABEL_CLASS}>
                      Parent / Registered Company Name <span className="text-gray-400 font-normal text-xs">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      name="parentCompanyName"
                      value={formData.parentCompanyName || ''}
                      onChange={handleInputChange}
                      placeholder="Write as per legal document"
                      className={getInputClass('parentCompanyName') + " text-xs sm:text-sm"}
                    />
                    <p className="text-[11px] text-gray-400 font-normal leading-tight">
                      If you have different company name (Write as per legal document)
                    </p>
                  </div>
                </div>
                <input type="file" name="govtProof" ref={govtProofInputRef} onChange={handleGovtProofUpload} accept="image/*,application/pdf" className="hidden" />

            {/* Row 3: Brand Logo & Profile Banner (compact height h-24) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              <div className="space-y-1">
                <label className={LABEL_CLASS}>Set Brand Logo<span className="text-red-500">*</span></label>
                {compressingLogo ? (
                  <div className="border border-[#2ab391]/40 bg-teal-50/60 rounded-lg p-3 flex items-center justify-between h-24 select-none">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Loader size={16} className="animate-spin text-[#2ab391] shrink-0" />
                      <div className="truncate">
                        <p className="text-xs font-bold text-gray-800">Optimizing Logo...</p>
                        <p className="text-[10px] text-gray-500 font-medium truncate">{compressingLogo.message}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <div className="w-14 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                        <div className="bg-[#2ab391] h-1.5 rounded-full transition-all duration-200" style={{ width: `${compressingLogo.progress}%` }} />
                      </div>
                      <span className="text-xs font-bold text-[#2ab391] w-8 text-right">{compressingLogo.progress}%</span>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => logoInputRef.current?.click()}
                    className={`border border-dashed rounded-lg p-2 flex flex-col items-center justify-center h-24 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors relative overflow-hidden ${isSubmitted && fieldErrors.brandLogo ? 'border-red-400 bg-red-50/10' : 'border-gray-300'}`}
                  >
                    {brandLogo ? (
                      <div className="relative w-full h-full flex items-center justify-center bg-gray-50">
                        <img
                          src={typeof brandLogo === 'string' ? getImageUrl(brandLogo) : URL.createObjectURL(brandLogo)}
                          alt="Brand Logo Preview"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity text-white text-xs font-semibold">
                          Change Logo
                        </div>
                      </div>
                    ) : (
                      <>
                        <Upload className={isSubmitted && fieldErrors.brandLogo ? "text-red-400 mb-1" : "text-[#2ab391] mb-1"} size={20} />
                        <p className={`text-xs font-medium ${isSubmitted && fieldErrors.brandLogo ? 'text-red-500' : 'text-gray-700'}`}>Click to upload brand logo</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">PNG, JPG • Max 5MB</p>
                      </>
                    )}
                  </div>
                )}
                <input type="file" name="brandLogo" ref={logoInputRef} onChange={handleLogoUpload} accept="image/*" className="hidden" />
                {isSubmitted && fieldErrors.brandLogo && (
                  <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                    <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                    {fieldErrors.brandLogo}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label className={LABEL_CLASS}>Profile Banner (Best Fit 450x350)<span className="text-red-500">*</span></label>
                {compressingBanner ? (
                  <div className="border border-[#2ab391]/40 bg-teal-50/60 rounded-lg p-3 flex items-center justify-between h-24 select-none">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Loader size={16} className="animate-spin text-[#2ab391] shrink-0" />
                      <div className="truncate">
                        <p className="text-xs font-bold text-gray-800">Optimizing Banner...</p>
                        <p className="text-[10px] text-gray-500 font-medium truncate">{compressingBanner.message}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <div className="w-14 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                        <div className="bg-[#2ab391] h-1.5 rounded-full transition-all duration-200" style={{ width: `${compressingBanner.progress}%` }} />
                      </div>
                      <span className="text-xs font-bold text-[#2ab391] w-8 text-right">{compressingBanner.progress}%</span>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => bannerInputRef.current?.click()}
                    className={`border border-dashed rounded-lg p-2 flex flex-col items-center justify-center h-24 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors relative overflow-hidden ${isSubmitted && fieldErrors.profileBanner ? 'border-red-400 bg-red-50/10' : 'border-gray-300'}`}
                  >
                    {profileBanner ? (
                      <div className="relative w-full h-full flex items-center justify-center bg-gray-50">
                        <img
                          src={typeof profileBanner === 'string' ? getImageUrl(profileBanner) : URL.createObjectURL(profileBanner)}
                          alt="Profile Banner Preview"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 flex items-center justify-center transition-opacity text-white text-xs font-semibold">
                          Change Image
                        </div>
                      </div>
                    ) : (
                      <>
                        <Upload className={isSubmitted && fieldErrors.profileBanner ? "text-red-400 mb-1" : "text-[#2ab391] mb-1"} size={20} />
                        <p className={`text-xs font-medium ${isSubmitted && fieldErrors.profileBanner ? 'text-red-500' : 'text-gray-700'}`}>Click to upload feature image</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">PNG, JPG • Max 5MB</p>
                      </>
                    )}
                  </div>
                )}
                <input type="file" name="profileBanner" ref={bannerInputRef} onChange={handleBannerUpload} accept="image/*" className="hidden" />
                {isSubmitted && fieldErrors.profileBanner && (
                  <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                    <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                    {fieldErrors.profileBanner}
                  </p>
                )}
              </div>
            </div>

            {/* Row 4: Image Gallery */}
            <div className="space-y-1">
              <label className={LABEL_CLASS + " flex justify-between"}>
                <span>Image Gallery</span>
                <span className="text-xs text-gray-400 font-medium normal-case tracking-normal">
                  {(() => {
                    const existSz = galleryImages.filter(img => typeof img === 'string').reduce((s, url) => s + (galleryUrlSizes[url] || 0), 0);
                    const newSz = galleryImages.filter(img => img instanceof File).reduce((s, f) => s + f.size, 0);
                    return `${formatFileSize(existSz + newSz)} / 15 MB used`;
                  })()}
                  {fetchingGallerySizes && <span className="ml-1 text-gray-300 text-[10px]">(loading...)</span>}
                </span>
              </label>

              {(membershipPlan === 'PROFESSIONAL' || membershipPlan === 'PREMIUM') && membershipStatus === 'ACTIVE' ? (
                <div>
                  {compressingGallery ? (
                    <div className="border border-[#2ab391]/40 bg-teal-50/60 rounded-lg p-3 flex items-center justify-between h-16 select-none mb-2">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Loader size={16} className="animate-spin text-[#2ab391] shrink-0" />
                        <div className="truncate">
                          <p className="text-xs font-bold text-gray-800">Optimizing Gallery Images...</p>
                          <p className="text-[10px] text-gray-500 font-medium truncate">{compressingGallery.message}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <div className="w-16 sm:w-20 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                          <div className="bg-[#2ab391] h-1.5 rounded-full transition-all duration-200" style={{ width: `${compressingGallery.progress}%` }} />
                        </div>
                        <span className="text-xs font-bold text-[#2ab391] w-8 text-right">{compressingGallery.progress}%</span>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={() => galleryInputRef.current?.click()}
                      className="border border-dashed border-gray-300 rounded-lg p-3 flex flex-col items-center justify-center h-16 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors mb-2"
                    >
                      <Upload className="text-[#2ab391] mb-1" size={16} />
                      <p className="text-xs font-medium text-gray-700">Click to upload gallery images</p>
                    </div>
                  )}
                  <input type="file" ref={galleryInputRef} onChange={handleGalleryUpload} accept="image/*" multiple className="hidden" />

                  {galleryImages.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto py-1">
                      {galleryImages.map((img, idx) => (
                        <div key={idx} className="relative w-10 h-10 flex-shrink-0 rounded overflow-hidden border border-gray-200">
                          <img
                            src={typeof img === 'string' ? getImageUrl(img) : URL.createObjectURL(img)}
                            alt={`gallery-${idx}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 24 24' fill='%23f1f5f9' stroke='%2394a3b8' stroke-width='1.5'><rect width='18' height='18' x='3' y='3' rx='2'/><circle cx='9' cy='9' r='2'/><path d='m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21'/></svg>";
                            }}
                          />
                          <button type="button" onClick={() => removeGalleryImage(idx)} className="absolute top-0 right-0 bg-red-500 text-white rounded-bl-md p-0.5"><X size={10} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="border border-dashed border-gray-300 bg-gray-50 rounded-lg p-2.5 h-20 flex flex-col justify-center items-center text-center">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <Lock size={14} className="text-gray-400" />
                    <h4 className="font-semibold text-gray-700 text-xs">
                      {membershipStatus === 'EXPIRED' ? 'Your plan has expired' : 'Upgrade to upload gallery images'}
                    </h4>
                  </div>
                  <p className="text-[10px] text-gray-500 mb-1 leading-tight">
                    {membershipStatus === 'EXPIRED'
                      ? 'Renew your membership to upload product/service images.'
                      : 'Gallery photos are available for Professional & Premium plan vendors.'}
                  </p>
                  <a
                    href="/my-account/upgrade-membership"
                    onClick={(e) => { e.preventDefault(); navigate('/my-account/upgrade-membership'); }}
                    className="bg-[#2ab391] text-white text-[10px] px-3 py-0.5 rounded font-medium hover:bg-[#23977a]"
                  >
                    {membershipStatus === 'EXPIRED' ? 'Renew Plan' : 'Upgrade Plan'}
                  </a>
                </div>
              )}
            </div>

            {/* Row 5: About Company */}
            <div className="space-y-1">
              <label className={LABEL_CLASS}>About Company<span className="text-red-500">*</span></label>
              <textarea
                name="aboutCompany"
                value={formData.aboutCompany}
                onChange={handleInputChange}
                onBlur={handleBlur}
                rows={3}
                placeholder="Important keyword in about us will help you in ranking"
                className={getInputClass('aboutCompany', true) + " resize-none h-20 min-h-[75px] py-2"}
              ></textarea>
              {isSubmitted && fieldErrors.aboutCompany && (
                <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                  <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                  {fieldErrors.aboutCompany}
                </p>
              )}
            </div>
          </section>

          <hr className="border-gray-100" />

          {/* 02 Contact Info */}
          <section className="space-y-3">
            <h3 className="text-[#2ab391] font-semibold text-sm tracking-wide mb-2">02 <span className="text-gray-800 font-semibold ml-1">Contact Info</span></h3>

            {/* Row 1: Contact No + Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              <div className="space-y-1">
                <label className={LABEL_CLASS}>Contact No.<span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="phone"
                  maxLength={12}
                  value={formData.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    setFormData(prev => ({ ...prev, phone: val }));
                  }}
                  onBlur={handleBlur}
                  placeholder="Enter phone number"
                  className={getInputClass('phone')}
                />
                {isSubmitted && fieldErrors.phone && (
                  <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                    <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                    {fieldErrors.phone}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <label className={LABEL_CLASS}>Email Address<span className="text-red-500">*</span></label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  placeholder="Enter email address"
                  className={getInputClass('email')}
                />
                {isSubmitted && fieldErrors.email && (
                  <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                    <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                    {fieldErrors.email}
                  </p>
                )}
              </div>
            </div>

            {/* Row 2: Address */}
            <div className="space-y-1">
              <label className={LABEL_CLASS}>Address<span className="text-red-500">*</span></label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="Enter address"
                className={getInputClass('address')}
              />
              {isSubmitted && fieldErrors.address && (
                <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                  <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                  {fieldErrors.address}
                </p>
              )}
            </div>

            {/* Row 3: State + City (Single line on mobile & desktop) */}
            <div className="grid grid-cols-2 gap-3 sm:gap-3.5">
              <div ref={stateRef} className="relative space-y-1 min-w-0">
                <label className={LABEL_CLASS}>State<span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={(e) => {
                      setFormData({ ...formData, state: e.target.value, city: '' });
                      setShowStateDropdown(true);
                    }}
                    onFocus={() => setShowStateDropdown(true)}
                    onBlur={handleBlur}
                    placeholder="Search state"
                    className={getInputClass('state') + " pr-8 text-xs sm:text-sm"}
                  />
                  <div
                    onClick={() => setShowStateDropdown(!showStateDropdown)}
                    className="absolute inset-y-0 right-0 pr-2 flex items-center cursor-pointer text-gray-400 hover:text-gray-600"
                  >
                    <ChevronDown size={16} />
                  </div>
                </div>
                {showStateDropdown && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredStates.length > 0 ? (
                      filteredStates.map(s => (
                        <div
                          key={s}
                          onClick={() => handleStateSelect(s)}
                          className="px-3 py-2 text-xs sm:text-sm text-gray-700 hover:bg-[#eaf7f4] hover:text-[#2ab391] cursor-pointer transition-colors"
                        >
                          {s}
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-xs text-gray-400">No states found</div>
                    )}
                  </div>
                )}
                {isSubmitted && fieldErrors.state && (
                  <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                    <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                    {fieldErrors.state}
                  </p>
                )}
              </div>

              <div ref={cityRef} className="relative space-y-1 min-w-0">
                <label className={LABEL_CLASS}>City<span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={(e) => {
                      setFormData({ ...formData, city: e.target.value });
                      setShowCityDropdown(true);
                    }}
                    onFocus={() => {
                      if (formData.state) setShowCityDropdown(true);
                    }}
                    onBlur={handleBlur}
                    disabled={!formData.state}
                    placeholder={formData.state ? "Search city" : "Select State First"}
                    className={getInputClass('city') + " pr-8 text-xs sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"}
                  />
                  <div
                    onClick={() => {
                      if (formData.state) setShowCityDropdown(!showCityDropdown);
                    }}
                    className={`absolute inset-y-0 right-0 pr-2 flex items-center ${formData.state ? 'cursor-pointer text-gray-400 hover:text-gray-600' : 'text-gray-300'}`}
                  >
                    <ChevronDown size={16} />
                  </div>
                </div>
                {showCityDropdown && formData.state && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {loadingCities ? (
                      <div className="px-3 py-2 text-xs text-gray-500">Loading cities...</div>
                    ) : filteredCities.length > 0 ? (
                      filteredCities.map(c => (
                        <div
                          key={c}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, city: c }));
                            setShowCityDropdown(false);
                          }}
                          className="px-3 py-2 text-xs sm:text-sm text-gray-700 hover:bg-[#eaf7f4] hover:text-[#2ab391] cursor-pointer transition-colors"
                        >
                          {c}
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-xs text-gray-400">No cities found</div>
                    )}
                  </div>
                )}
                {isSubmitted && fieldErrors.city && (
                  <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                    <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                    {fieldErrors.city}
                  </p>
                )}
              </div>
            </div>

            {/* Row 4: Area / Locality + Zipcode / Postcode (Single line on mobile & desktop) */}
            <div className="grid grid-cols-2 gap-3 sm:gap-3.5">
              <div className="space-y-1 min-w-0">
                <label className={LABEL_CLASS}>Area / Locality<span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="area"
                  value={formData.area}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  placeholder="Enter area or locality"
                  className={getInputClass('area') + " text-xs sm:text-sm"}
                />
                {isSubmitted && fieldErrors.area && (
                  <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                    <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                    {fieldErrors.area}
                  </p>
                )}
              </div>

              <div className="space-y-1 min-w-0">
                <label className={LABEL_CLASS}>Zipcode / Postcode<span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="zipcode"
                  maxLength={6}
                  value={formData.zipcode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    setFormData(prev => ({ ...prev, zipcode: val }));
                  }}
                  onBlur={handleBlur}
                  placeholder="Enter zipcode"
                  className={getInputClass('zipcode') + " text-xs sm:text-sm"}
                />
                {isSubmitted && fieldErrors.zipcode && (
                  <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                    <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                    {fieldErrors.zipcode}
                  </p>
                )}
              </div>
            </div>

            {apiError && (
              <div className="p-2.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-xs flex items-center justify-between">
                <span>⚠️ {apiError}</span>
                <button type="button" onClick={() => setApiError('')} className="text-amber-600 hover:text-amber-800 font-bold ml-2">×</button>
              </div>
            )}

            {/* Row 5: Website */}
            <div className="space-y-1">
              <label className={LABEL_CLASS}>Website</label>
              <input type="text" name="website" value={formData.website} onChange={handleInputChange} placeholder="https://yourwebsite.com" className={INPUT_CLASS} />
            </div>
          </section>

          <hr className="border-gray-100" />

          {/* 03 Categories & Tags */}
          <section className="space-y-3">
            <h3 className="text-[#2ab391] font-semibold text-sm tracking-wide mb-2">03 <span className="text-gray-800 font-semibold ml-1">Categories & Tags</span></h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              <div className="space-y-1 w-full">
                <div className="flex justify-between items-center">
                  <label className={LABEL_CLASS + " mb-0"}>Category<span className="text-red-500">*</span></label>
                  <button
                    type="button"
                    onClick={() => setShowCategoryRequestModal(true)}
                    className="text-xs font-semibold text-[#2ab391] hover:underline cursor-pointer focus:outline-none"
                  >
                    Request New Category
                  </button>
                </div>
                <div className="relative">
                  <select
                    name="category"
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    onBlur={handleBlur}
                    className={getInputClass('category') + " appearance-none cursor-pointer pr-10"}
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
                    <ChevronDown size={18} />
                  </div>
                </div>
                {isSubmitted && fieldErrors.category && (
                  <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                    <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                    {fieldErrors.category}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className={LABEL_CLASS}>Brands</label>
                {!selectedCategory ? (
                  <div className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-400 text-xs h-[38px] md:h-[40px] flex items-center">
                    Select category to load brands
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <input
                        type="text"
                        name="brands"
                        placeholder="Search brands..."
                        value={brandSearch}
                        onChange={(e) => setBrandSearch(e.target.value)}
                        className={INPUT_CLASS + " pl-9 pr-3"}
                      />
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={15} />
                    </div>
                    <div className="border border-gray-200 rounded-lg p-2.5 bg-gray-50/50 max-h-40 overflow-y-auto mt-1">
                      {(() => {
                        const filteredBrands = (activeBrands || []).filter(brand =>
                          brand && brand.name && typeof brand.name === 'string' && brand.name.toLowerCase().includes((brandSearch || '').toLowerCase())
                        );
                        return (
                          <div className="flex flex-wrap gap-1.5">
                            {filteredBrands.map(brand => {
                              const isSelected = selectedBrands.includes(brand.id);
                              return (
                                <button
                                  key={brand.id}
                                  type="button"
                                  onClick={() => handleBrandToggle(brand.id)}
                                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-150 flex items-center gap-1 cursor-pointer select-none ${isSelected
                                    ? 'bg-[#eaf7f4] border-[#2ab391] text-[#2ab391] font-semibold shadow-2xs'
                                    : 'bg-white border-gray-300 text-gray-700 hover:border-[#2ab391] hover:text-[#2ab391]'
                                    }`}
                                >
                                  {isSelected ? <Check size={11} className="text-[#2ab391]" /> : <Plus size={11} className="text-gray-400" />}
                                  <span>{brand.name}</span>
                                </button>
                              );
                            })}
                            {filteredBrands.length === 0 && (
                              <span className="text-xs text-gray-400 italic w-full text-center py-2 block">
                                {activeBrands.length === 0 ? "No brands available for this category." : "No matching brands found."}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </>
                )}
              </div>

              <div className="space-y-1">
                <label className={LABEL_CLASS}>Tags<span className="text-red-500">*</span></label>
                {!selectedCategory ? (
                  <div className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-400 text-xs h-[38px] md:h-[40px] flex items-center">
                    Select category to load tags
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <input
                        type="text"
                        name="tags"
                        placeholder="Search tags..."
                        value={tagSearch}
                        onChange={(e) => setTagSearch(e.target.value)}
                        className={INPUT_CLASS + " pl-9 pr-3"}
                      />
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={15} />
                    </div>
                    <div className={`border rounded-lg p-2.5 bg-gray-50/50 max-h-40 overflow-y-auto mt-1 ${isSubmitted && fieldErrors.tags ? 'border-red-400 bg-red-50/10' : 'border-gray-200'
                      }`}>
                      {(() => {
                        const filteredTags = (activeTags || []).filter(tag =>
                          tag && tag.name && typeof tag.name === 'string' && tag.name.toLowerCase().includes((tagSearch || '').toLowerCase())
                        );
                        return (
                          <div className="flex flex-wrap gap-1.5">
                            {filteredTags.map(tag => {
                              const isSelected = selectedTags.includes(tag.id);
                              return (
                                <button
                                  key={tag.id}
                                  type="button"
                                  onClick={() => handleTagToggle(tag.id)}
                                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-150 flex items-center gap-1 cursor-pointer select-none ${isSelected
                                    ? 'bg-[#eaf7f4] border-[#2ab391] text-[#2ab391] font-semibold shadow-2xs'
                                    : 'bg-white border-gray-300 text-gray-700 hover:border-[#2ab391] hover:text-[#2ab391]'
                                    }`}
                                >
                                  {isSelected ? <Check size={11} className="text-[#2ab391]" /> : <Plus size={11} className="text-gray-400" />}
                                  <span>{tag.name}</span>
                                </button>
                              );
                            })}
                            {filteredTags.length === 0 && (
                              <span className="text-xs text-gray-400 italic w-full text-center py-2 block">
                                {activeTags.length === 0 ? "No tags available for this category." : "No matching tags found."}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    {isSubmitted && fieldErrors.tags && (
                      <p className="text-xs text-red-500 font-normal mt-1 flex items-center gap-1">
                        <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-medium flex items-center justify-center flex-shrink-0">!</span>
                        {fieldErrors.tags}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>

          <hr className="border-gray-100" />

          {/* Additional Details Toggle Prompt (Business Hours & Social Profiles) */}
          {!showAdditionalDetails ? (
            <div className="py-1">
              <button
                type="button"
                onClick={() => setShowAdditionalDetails(true)}
                className="text-[#2ab391] hover:text-[#219074] py-1 text-xs sm:text-sm font-semibold flex items-center gap-2 transition-colors cursor-pointer group focus:outline-none"
              >
                <Plus size={18} className="transition-transform group-hover:scale-110 flex-shrink-0" />
                <span className="hover:underline text-left">Add Business Hours & Social Media Profiles</span>
                <ChevronDown size={18} className="flex-shrink-0" />
              </button>
            </div>
          ) : (
            <>
              {/* 04 Business Hours */}
              <section className="space-y-3">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-[#2ab391] font-semibold text-sm tracking-wide">04 <span className="text-gray-800 font-semibold ml-1">Business Hours</span></h3>
                  <button
                    type="button"
                    onClick={() => setShowAdditionalDetails(false)}
                    className="text-xs text-gray-400 hover:text-gray-600 font-medium flex items-center gap-1 cursor-pointer transition-colors"
                  >
                    <span>Hide Details</span>
                    <ChevronUp size={14} />
                  </button>
                </div>

                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { label: '24 / 7', value: 'ALWAYS_OPEN' },
                    { label: 'Mon - Sat', value: 'MON_TO_SAT' },
                    { label: 'Mon - Fri', value: 'MON_TO_FRI' },
                    { label: 'Custom', value: 'CUSTOM' }
                  ].map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setBusinessHoursType(type.value)}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors border ${businessHoursType === type.value
                        ? 'bg-[#2ab391] text-white border-[#2ab391]'
                        : 'border-gray-300 text-gray-600 hover:border-[#2ab391] hover:text-[#2ab391]'
                        }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>

                <div className="w-full">
                  {businessHoursType === 'ALWAYS_OPEN' && (
                    <div className="bg-[#e8f7f0] border border-[#a3e4c7] rounded-lg p-2.5 flex items-center gap-2 w-fit text-[#1a7f5a]">
                      <Clock size={16} />
                      <span className="font-medium text-xs">Open 24 hours, 7 days a week</span>
                    </div>
                  )}

                  {businessHoursType === 'MON_TO_SAT' && (
                    <div className="space-y-2.5 w-full max-w-2xl">
                      <div>
                        <p className="text-[11px] font-bold text-gray-400 mb-1.5 uppercase tracking-wider">Main Hours (Mon - Sat)</p>
                        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center bg-slate-50/80 border border-gray-200 p-2 rounded-xl shadow-2xs w-full overflow-hidden">
                          <div className="bg-[#1e293b] text-white px-3 py-1.5 rounded-lg font-semibold text-xs w-full sm:w-28 text-center flex items-center justify-center flex-shrink-0 shadow-2xs">Mon - Sat</div>
                          <div className="flex items-center gap-1.5 sm:gap-2 w-full min-w-0 sm:flex-1">
                            <select value={businessHours.mainHours.openTime} onChange={(e) => setBusinessHours({ ...businessHours, mainHours: { ...businessHours.mainHours, openTime: e.target.value } })} className="border border-gray-300 rounded-lg px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs focus:outline-none focus:ring-1 focus:ring-[#2ab391] min-w-0 flex-1 bg-white text-gray-700 h-[36px] font-medium cursor-pointer hover:border-[#2ab391]">
                              {TIME_OPTIONS.map(time => <option key={time} value={time}>{time}</option>)}
                            </select>
                            <span className="text-gray-400 text-[9px] sm:text-[10px] font-bold uppercase px-1 sm:px-1.5 py-0.5 bg-gray-200/60 rounded text-center flex-shrink-0">to</span>
                            <select value={businessHours.mainHours.closeTime} onChange={(e) => setBusinessHours({ ...businessHours, mainHours: { ...businessHours.mainHours, closeTime: e.target.value } })} className="border border-gray-300 rounded-lg px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs focus:outline-none focus:ring-1 focus:ring-[#2ab391] min-w-0 flex-1 bg-white text-gray-700 h-[36px] font-medium cursor-pointer hover:border-[#2ab391]">
                              {TIME_OPTIONS.map(time => <option key={time} value={time}>{time}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className="text-[11px] font-bold text-gray-400 mb-1.5 uppercase tracking-wider">Sunday Hours</p>
                        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center bg-slate-50/80 border border-gray-200 p-2 rounded-xl shadow-2xs w-full overflow-hidden">
                          <div className="bg-[#475569] text-white px-3 py-1.5 rounded-lg font-semibold text-xs w-full sm:w-28 text-center flex items-center justify-center flex-shrink-0 shadow-2xs">Sunday</div>
                          <div className="flex items-center gap-1.5 sm:gap-2 w-full min-w-0 sm:flex-1">
                            <select value={businessHours.sundayHours.openTime} onChange={(e) => setBusinessHours({ ...businessHours, sundayHours: { ...businessHours.sundayHours, openTime: e.target.value } })} className="border border-gray-300 rounded-lg px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs focus:outline-none focus:ring-1 focus:ring-[#2ab391] min-w-0 flex-1 bg-white text-gray-700 h-[36px] font-medium cursor-pointer hover:border-[#2ab391]">
                              {TIME_OPTIONS.map(time => <option key={time} value={time}>{time}</option>)}
                            </select>
                            <span className="text-gray-400 text-[9px] sm:text-[10px] font-bold uppercase px-1 sm:px-1.5 py-0.5 bg-gray-200/60 rounded text-center flex-shrink-0">to</span>
                            <select value={businessHours.sundayHours.closeTime} onChange={(e) => setBusinessHours({ ...businessHours, sundayHours: { ...businessHours.sundayHours, closeTime: e.target.value } })} className="border border-gray-300 rounded-lg px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs focus:outline-none focus:ring-1 focus:ring-[#2ab391] min-w-0 flex-1 bg-white text-gray-700 h-[36px] font-medium cursor-pointer hover:border-[#2ab391]">
                              {TIME_OPTIONS.map(time => <option key={time} value={time}>{time}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {businessHoursType === 'MON_TO_FRI' && (
                    <div className="space-y-2.5 w-full max-w-2xl">
                      <div>
                        <p className="text-[11px] font-bold text-gray-400 mb-1.5 uppercase tracking-wider">Main Hours (Mon - Fri)</p>
                        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center bg-slate-50/80 border border-gray-200 p-2 rounded-xl shadow-2xs w-full overflow-hidden">
                          <div className="bg-[#1e293b] text-white px-3 py-1.5 rounded-lg font-semibold text-xs w-full sm:w-28 text-center flex items-center justify-center flex-shrink-0 shadow-2xs">Mon - Fri</div>
                          <div className="flex items-center gap-1.5 sm:gap-2 w-full min-w-0 sm:flex-1">
                            <select value={businessHours.mainHours.openTime} onChange={(e) => setBusinessHours({ ...businessHours, mainHours: { ...businessHours.mainHours, openTime: e.target.value } })} className="border border-gray-300 rounded-lg px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs focus:outline-none focus:ring-1 focus:ring-[#2ab391] min-w-0 flex-1 bg-white text-gray-700 h-[36px] font-medium cursor-pointer hover:border-[#2ab391]">
                              {TIME_OPTIONS.map(time => <option key={time} value={time}>{time}</option>)}
                            </select>
                            <span className="text-gray-400 text-[9px] sm:text-[10px] font-bold uppercase px-1 sm:px-1.5 py-0.5 bg-gray-200/60 rounded text-center flex-shrink-0">to</span>
                            <select value={businessHours.mainHours.closeTime} onChange={(e) => setBusinessHours({ ...businessHours, mainHours: { ...businessHours.mainHours, closeTime: e.target.value } })} className="border border-gray-300 rounded-lg px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs focus:outline-none focus:ring-1 focus:ring-[#2ab391] min-w-0 flex-1 bg-white text-gray-700 h-[36px] font-medium cursor-pointer hover:border-[#2ab391]">
                              {TIME_OPTIONS.map(time => <option key={time} value={time}>{time}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className="text-[11px] font-bold text-gray-400 mb-1.5 uppercase tracking-wider">Weekend Hours</p>
                        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center bg-slate-50/80 border border-gray-200 p-2 rounded-xl shadow-2xs mb-2 w-full overflow-hidden">
                          <div className="bg-[#475569] text-white px-3 py-1.5 rounded-lg font-semibold text-xs w-full sm:w-28 text-center flex items-center justify-center flex-shrink-0 shadow-2xs">Saturday</div>
                          <div className="flex items-center gap-1.5 sm:gap-2 w-full min-w-0 sm:flex-1">
                            <select value={businessHours.saturdayHours.openTime} onChange={(e) => setBusinessHours({ ...businessHours, saturdayHours: { ...businessHours.saturdayHours, openTime: e.target.value } })} className="border border-gray-300 rounded-lg px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs focus:outline-none focus:ring-1 focus:ring-[#2ab391] min-w-0 flex-1 bg-white text-gray-700 h-[36px] font-medium cursor-pointer hover:border-[#2ab391]">
                              {TIME_OPTIONS.map(time => <option key={time} value={time}>{time}</option>)}
                            </select>
                            <span className="text-gray-400 text-[9px] sm:text-[10px] font-bold uppercase px-1 sm:px-1.5 py-0.5 bg-gray-200/60 rounded text-center flex-shrink-0">to</span>
                            <select value={businessHours.saturdayHours.closeTime} onChange={(e) => setBusinessHours({ ...businessHours, saturdayHours: { ...businessHours.saturdayHours, closeTime: e.target.value } })} className="border border-gray-300 rounded-lg px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs focus:outline-none focus:ring-1 focus:ring-[#2ab391] min-w-0 flex-1 bg-white text-gray-700 h-[36px] font-medium cursor-pointer hover:border-[#2ab391]">
                              {TIME_OPTIONS.map(time => <option key={time} value={time}>{time}</option>)}
                            </select>
                          </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center bg-slate-50/80 border border-gray-200 p-2 rounded-xl shadow-2xs w-full overflow-hidden">
                          <div className="bg-[#475569] text-white px-3 py-1.5 rounded-lg font-semibold text-xs w-full sm:w-28 text-center flex items-center justify-center flex-shrink-0 shadow-2xs">Sunday</div>
                          <div className="flex items-center gap-1.5 sm:gap-2 w-full min-w-0 sm:flex-1">
                            <select value={businessHours.sundayHours.openTime} onChange={(e) => setBusinessHours({ ...businessHours, sundayHours: { ...businessHours.sundayHours, openTime: e.target.value } })} className="border border-gray-300 rounded-lg px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs focus:outline-none focus:ring-1 focus:ring-[#2ab391] min-w-0 flex-1 bg-white text-gray-700 h-[36px] font-medium cursor-pointer hover:border-[#2ab391]">
                              {TIME_OPTIONS.map(time => <option key={time} value={time}>{time}</option>)}
                            </select>
                            <span className="text-gray-400 text-[9px] sm:text-[10px] font-bold uppercase px-1 sm:px-1.5 py-0.5 bg-gray-200/60 rounded text-center flex-shrink-0">to</span>
                            <select value={businessHours.sundayHours.closeTime} onChange={(e) => setBusinessHours({ ...businessHours, sundayHours: { ...businessHours.sundayHours, closeTime: e.target.value } })} className="border border-gray-300 rounded-lg px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs focus:outline-none focus:ring-1 focus:ring-[#2ab391] min-w-0 flex-1 bg-white text-gray-700 h-[36px] font-medium cursor-pointer hover:border-[#2ab391]">
                              {TIME_OPTIONS.map(time => <option key={time} value={time}>{time}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {businessHoursType === 'CUSTOM' && (
                    <div className="space-y-2 w-full max-w-2xl">
                      {businessHours.customHours.map((customHour, index) => (
                        <div key={index} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center bg-slate-50/80 border border-gray-200 p-2 rounded-xl shadow-2xs w-full overflow-hidden">
                          <select
                            value={customHour.day}
                            onChange={(e) => updateCustomHour(index, 'day', e.target.value)}
                            className={`border rounded-lg px-2.5 py-1 text-xs focus:outline-none w-full sm:w-28 font-semibold bg-white cursor-pointer h-[36px] ${customHour.day !== 'Day' ? 'border-[#2ab391] text-[#2ab391]' : 'border-gray-300 text-gray-700'}`}
                          >
                            {DAYS_OF_WEEK.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>

                          <div className="flex items-center gap-1.5 sm:gap-2 w-full min-w-0 sm:flex-1">
                            <select value={customHour.openTime} onChange={(e) => updateCustomHour(index, 'openTime', e.target.value)} className="border border-gray-300 rounded-lg px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs focus:outline-none focus:ring-1 focus:ring-[#2ab391] min-w-0 flex-1 bg-white text-gray-700 h-[36px] font-medium cursor-pointer hover:border-[#2ab391]">
                              {TIME_OPTIONS.map(time => <option key={time} value={time}>{time}</option>)}
                            </select>

                            <span className="text-gray-400 text-[9px] sm:text-[10px] font-bold uppercase px-1 sm:px-1.5 py-0.5 bg-gray-200/60 rounded text-center flex-shrink-0">to</span>

                            <select value={customHour.closeTime} onChange={(e) => updateCustomHour(index, 'closeTime', e.target.value)} className="border border-gray-300 rounded-lg px-1.5 sm:px-2.5 py-1 text-[11px] sm:text-xs focus:outline-none focus:ring-1 focus:ring-[#2ab391] min-w-0 flex-1 bg-white text-gray-700 h-[36px] font-medium cursor-pointer hover:border-[#2ab391]">
                              {TIME_OPTIONS.map(time => <option key={time} value={time}>{time}</option>)}
                            </select>

                            <button type="button" onClick={() => removeCustomHour(index)} className="border border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 p-1.5 rounded-lg transition-colors flex-shrink-0 cursor-pointer">
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={addCustomHour}
                        className="flex items-center gap-1.5 border border-dashed border-[#2ab391] text-[#2ab391] bg-[#e8f7f0] px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-[#d1f0e1] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        disabled={businessHours.customHours.length >= 7}
                      >
                        <Plus size={14} /> Add hours
                      </button>
                    </div>
                  )}
                </div>
              </section>

              <hr className="border-gray-100" />

              {/* 05 Social Profile */}
              <section className="space-y-3">
                <h3 className="text-[#2ab391] font-semibold text-sm tracking-wide mb-2">05 <span className="text-gray-800 font-semibold ml-1">Social Profile</span></h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <label className={LABEL_CLASS}>Instagram</label>
                    <input type="text" name="instagram" value={formData.instagram} onChange={handleInputChange} placeholder="Enter instagram link" className={INPUT_CLASS} />
                  </div>
                  <div className="space-y-1">
                    <label className={LABEL_CLASS}>Facebook</label>
                    <input type="text" name="facebook" value={formData.facebook} onChange={handleInputChange} placeholder="Enter Facebook link" className={INPUT_CLASS} />
                  </div>
                  <div className="space-y-1">
                    <label className={LABEL_CLASS}>LinkedIn</label>
                    <input type="text" name="linkedin" value={formData.linkedin} onChange={handleInputChange} placeholder="Enter linkedin link" className={INPUT_CLASS} />
                  </div>
                  <div className="space-y-1">
                    <label className={LABEL_CLASS}>X</label>
                    <input type="text" name="twitter" value={formData.twitter} onChange={handleInputChange} placeholder="Enter X link" className={INPUT_CLASS} />
                  </div>
                </div>
              </section>
            </>
          )}

          <div className="pt-3 flex items-center gap-3 w-full sm:w-auto">
            <button
              type="button"
              onClick={resetForm}
              className="flex-1 sm:flex-none inline-flex items-center justify-center px-5 sm:px-6 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all text-xs sm:text-sm font-semibold whitespace-nowrap cursor-pointer shadow-2xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !!compressingBanner || !!compressingLogo || !!compressingGovtProof || !!compressingGallery}
              className="flex-1 sm:flex-none inline-flex items-center justify-center px-6 sm:px-8 py-2.5 bg-[#2ab391] text-white rounded-xl hover:bg-[#23977a] active:bg-[#1d8268] transition-all text-xs sm:text-sm font-semibold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shadow-2xs"
            >
              {submitting ? 'Submitting...' : (compressingBanner || compressingLogo || compressingGovtProof || compressingGallery ? 'Optimizing Media...' : (editingListingId ? 'Update Listing' : 'Submit Listing'))}
            </button>
          </div>
          </>
          )}
        </form>
      )}

      {showCategoryRequestModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-[9999] flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-gray-150 overflow-hidden relative flex flex-col my-8 md:my-16 animate-in fade-in zoom-in-95 duration-200">
            {/* Close button */}
            <button
              type="button"
              onClick={() => {
                setShowCategoryRequestModal(false);
                setReqCategoryName('');
                setReqCategoryDesc('');
                setReqCategoryNameError('');
                setReqCategoryDescError('');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-650 hover:bg-gray-100 p-1.5 rounded-full transition-colors cursor-pointer"
            >
              <X size={18} />
            </button>

            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex flex-col gap-1 text-left bg-gray-50/50">
              <span className="text-[10px] uppercase font-bold text-[#2ab391] tracking-wider">New Category Request</span>
              <h3 className="text-lg font-bold text-gray-800">Suggest a New Category</h3>
              <p className="text-xs text-gray-500 font-medium">If your business doesn't fit in the existing categories, request a new one here.</p>
            </div>

            {/* Form */}
            <form onSubmit={handleCategoryRequestSubmit} className="flex flex-col flex-1 overflow-y-auto">
              <div className="p-6 space-y-4 text-left">
                {/* Category Name */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-550 uppercase tracking-wide">Requested Category Name<span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={reqCategoryName}
                    placeholder="e.g. Interior Designer, Wallpaper Specialist"
                    onChange={(e) => {
                      setReqCategoryName(e.target.value);
                      if (e.target.value.trim().length >= 2) setReqCategoryNameError('');
                    }}
                    className={`w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 focus:border-[#2ab391] ${reqCategoryNameError ? 'border-red-500 font-medium text-xs' : 'border-gray-300'}`}
                  />
                  {reqCategoryNameError && <p className="text-xs text-red-500 font-medium">{reqCategoryNameError}</p>}
                </div>

                {/* Description / Reason */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-550 uppercase tracking-wide">Reason & Description<span className="text-red-500">*</span></label>
                  <textarea
                    rows={4}
                    value={reqCategoryDesc}
                    placeholder="Describe what services this category includes and why you need it."
                    onChange={(e) => {
                      setReqCategoryDesc(e.target.value);
                      if (e.target.value.trim().length >= 10) setReqCategoryDescError('');
                    }}
                    className={`w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 focus:border-[#2ab391] resize-none ${reqCategoryDescError ? 'border-red-500 font-medium text-xs' : 'border-gray-300'}`}
                  />
                  {reqCategoryDescError && <p className="text-xs text-red-500 font-medium">{reqCategoryDescError}</p>}
                </div>
              </div>

              {/* Actions Footer */}
              <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCategoryRequestModal(false);
                    setReqCategoryName('');
                    setReqCategoryDesc('');
                    setReqCategoryNameError('');
                    setReqCategoryDescError('');
                  }}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50 active:scale-[0.98] transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingCategoryRequest}
                  className="px-4 py-2 bg-[#2ab391] hover:bg-[#239f80] disabled:opacity-50 text-white rounded-lg text-sm font-semibold active:scale-[0.98] transition-all cursor-pointer shadow-sm flex items-center gap-1.5"
                >
                  {submittingCategoryRequest ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showClaimModal && matchedListing && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-[9999] flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl border border-gray-150 overflow-hidden relative flex flex-col my-8 md:my-16 animate-in fade-in zoom-in-95 duration-200">
            {/* Close button */}
            <button
              type="button"
              onClick={() => {
                setShowClaimModal(false);
                setClaimantDesignation('');
                setClaimComments('');
                setClaimProofFile(null);
                setClaimantNameError('');
                setClaimantPhoneError('');
                setClaimantDesignationError('');
                setClaimCommentsError('');
                setClaimProofFileError('');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-650 hover:bg-gray-100 p-1.5 rounded-full transition-colors cursor-pointer"
            >
              <X size={18} />
            </button>

            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex flex-col gap-1 text-left bg-gray-50/50">
              <span className="text-[10px] uppercase font-bold text-[#2ab391] tracking-wider">Verification Request</span>
              <h3 className="text-lg font-bold text-gray-800">Claim Business Ownership</h3>
              <p className="text-xs text-gray-500 font-medium">Please provide accurate verification details to claim <strong>{matchedListing.companyName}</strong>.</p>
            </div>

            {/* Form */}
            <form onSubmit={handleClaimSubmit} className="flex flex-col flex-1 overflow-y-auto max-h-[75vh]">
              <div className="p-6 space-y-4 text-left">
                {/* Claimant Name */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-550 uppercase tracking-wide">Claimant Full Name<span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={claimantName}
                    onChange={(e) => {
                      setClaimantName(e.target.value);
                      if (e.target.value.trim().length >= 2) setClaimantNameError('');
                    }}
                    placeholder="Enter your full name"
                    className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 bg-white transition-all text-gray-750 ${claimantNameError ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : 'border-gray-300 focus:ring-[#2ab391]/20 focus:border-[#2ab391]'
                      }`}
                  />
                  {claimantNameError && (
                    <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                      <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                      {claimantNameError}
                    </p>
                  )}
                </div>

                {/* Claimant Phone */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-550 uppercase tracking-wide">Contact Phone Number<span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    maxLength={10}
                    value={claimantPhone}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setClaimantPhone(val);
                      if (/^\d{10}$/.test(val)) setClaimantPhoneError('');
                    }}
                    placeholder="10-digit mobile number"
                    className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 bg-white transition-all text-gray-750 ${claimantPhoneError ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : 'border-gray-300 focus:ring-[#2ab391]/20 focus:border-[#2ab391]'
                      }`}
                  />
                  {claimantPhoneError && (
                    <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                      <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                      {claimantPhoneError}
                    </p>
                  )}
                </div>

                {/* Designation */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-550 uppercase tracking-wide">Designation / Role<span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={claimantDesignation}
                    onChange={(e) => {
                      setClaimantDesignation(e.target.value);
                      if (e.target.value.trim().length >= 2) setClaimantDesignationError('');
                    }}
                    placeholder="e.g. Business Owner, Store Manager"
                    className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 bg-white transition-all text-gray-750 ${claimantDesignationError ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : 'border-gray-300 focus:ring-[#2ab391]/20 focus:border-[#2ab391]'
                      }`}
                  />
                  {claimantDesignationError && (
                    <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                      <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                      {claimantDesignationError}
                    </p>
                  )}
                </div>

                {/* Comments / Description */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-550 uppercase tracking-wide">Description / Comments <span className="text-gray-400 font-normal">(Optional)</span></label>
                  <textarea
                    rows={3}
                    value={claimComments}
                    onChange={(e) => setClaimComments(e.target.value)}
                    placeholder="Provide additional details or references to verify your connection to this business."
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 focus:border-[#2ab391] bg-white transition-all text-gray-750 resize-none"
                  />
                </div>

                {/* Government Proof Document */}
                <div className="space-y-2">
                  <label className="block text-xs font-bold text-gray-550 uppercase tracking-wide">
                    Government Photo Proof Document
                    {(!pendingClaim || !pendingClaim.proofPhotoUrl) && <span className="text-red-500">*</span>}
                  </label>

                  {pendingClaim && pendingClaim.proofPhotoUrl && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center justify-between text-xs text-slate-650 mb-2">
                      <span className="truncate max-w-[250px]">
                        📄 Previous document: <a href={getImageUrl(pendingClaim.proofPhotoUrl)} target="_blank" rel="noreferrer" className="text-[#2ab391] hover:underline font-semibold">View uploaded proof</a>
                      </span>
                      <span className="text-[10px] text-gray-400 font-semibold">(Optional to upload new proof)</span>
                    </div>
                  )}

                  {compressingClaimProof ? (
                    <div className="border border-[#2ab391]/40 bg-teal-50/60 rounded-xl p-4 flex flex-col items-center justify-center relative h-28 select-none">
                      <Loader size={20} className="animate-spin text-[#2ab391] mb-1.5" />
                      <p className="text-xs font-bold text-gray-800">Optimizing proof document ({compressingClaimProof.progress}%)...</p>
                      <p className="text-[10px] text-gray-500 font-medium mt-0.5">{compressingClaimProof.message}</p>
                      <div className="w-24 bg-gray-200 rounded-full h-1.5 overflow-hidden mt-2">
                        <div className="bg-[#2ab391] h-1.5 rounded-full transition-all duration-200" style={{ width: `${compressingClaimProof.progress}%` }} />
                      </div>
                    </div>
                  ) : (
                    <div className={`border-2 border-dashed rounded-xl p-4 flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 cursor-pointer relative h-28 ${claimProofFileError ? 'border-red-400 bg-red-50/10' : 'border-gray-300'
                      }`}>
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          e.target.value = '';
                          const isFilePDF = isPDF(file);
                          if (isFilePDF) {
                            if (file.size > 5 * 1024 * 1024) {
                              console.warn('[Upload] Claim PDF too large — rejected:', { name: file.name, size: formatFileSize(file.size) });
                              toast.error('PDF files must be under 5MB. Please compress it manually.');
                              return;
                            }
                            setClaimProofFile(file);
                            setClaimProofFileError('');
                            return;
                          }
                          // Image — auto-compress if needed
                          setCompressingClaimProof({ progress: 10, message: 'Reading document...' });
                          try {
                            const compressed = await compressImageIfNeeded(file, 5 * 1024 * 1024, (p, msg) => {
                              setCompressingClaimProof({ progress: p, message: msg });
                            });
                            setClaimProofFile(compressed);
                            setClaimProofFileError('');
                          } catch (err) {
                            if (err.message === 'FILE_TOO_LARGE') {
                              toast.error('Image is too large. Please select an image under 5MB.');
                            } else {
                              console.error('[MediaCompressor] Claim proof compression failed:', err);
                              toast.warning('Could not auto-compress image. Please try a smaller file.');
                            }
                          } finally {
                            setCompressingClaimProof(null);
                          }
                        }}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                      />
                      <Upload className={claimProofFileError ? "text-red-400 mb-1" : "text-[#2ab391] mb-1"} size={20} />
                      <p className={`text-xs font-semibold ${claimProofFileError ? 'text-red-500' : 'text-gray-700'}`}>
                        {claimProofFile ? `Selected: ${claimProofFile.name}` : 'Click or drag to upload proof document'}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-0.5">GSTIN, Business License, or electricity bill (Max 5MB)</p>
                    </div>
                  )}
                  {claimProofFileError && (
                    <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
                      <span className="inline-block w-3.5 h-3.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">!</span>
                      {claimProofFileError}
                    </p>
                  )}
                </div>
              </div>

              {/* Footer Actions */}
              <div className="p-6 bg-slate-50 border-t border-gray-100 flex justify-end gap-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowClaimModal(false);
                    setClaimantDesignation('');
                    setClaimComments('');
                    setClaimProofFile(null);
                    setClaimantNameError('');
                    setClaimantPhoneError('');
                    setClaimantDesignationError('');
                    setClaimCommentsError('');
                    setClaimProofFileError('');
                  }}
                  disabled={submittingClaim}
                  className="px-5 py-2.5 border border-gray-300 text-gray-650 hover:text-gray-800 text-sm font-semibold rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50 cursor-pointer active:scale-98"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingClaim}
                  className="px-6 py-2.5 bg-[#2ab391] hover:bg-[#23977a] text-white text-sm font-bold rounded-xl shadow-md shadow-teal-500/10 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer active:scale-98"
                >
                  {submittingClaim ? 'Submitting...' : 'Submit Claim'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageListing;
